import React from 'react';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, Select, MenuItem, TextField, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  IconButton, Alert, Stack, Switch, FormControlLabel,
  CircularProgress, InputAdornment, Autocomplete, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ScienceIcon from '@mui/icons-material/Science';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import type { ICheckPlus, ICategory, ILabel } from '../../../shared/types.js';
import { Rating, EngineMode, UserRole } from '../../../shared/types.js';
import { applyOperator } from '../../../shared/ruleOperators.js';
import { colors } from '../../styles/theme.js';

/* ── Operator-Dropdown ───────────────────────────────────── */

/**
 * Die Operatorliste ist auf 26 Eintraege gewachsen und wuerde sonst ueber den
 * halben Bildschirm laufen. Gedeckelt auf gut zehn sichtbare Zeilen, der Rest
 * ist scrollbar.
 */
const OPERATOR_MENU_MAX_HEIGHT = 360;

const OPERATOR_MENU_PROPS = {
  disablePortal: true,
  PaperProps: { sx: { maxHeight: OPERATOR_MENU_MAX_HEIGHT } },
} as const;

/**
 * Die Operatorliste — eine Definition, beide Dropdowns.
 *
 * `label` bleibt kurz, damit die Liste schmal bleibt; die Erklaerung steckt in
 * `hint` und erscheint erst beim Hovern. Die frueheren Zusaetze direkt im Label
 * ("— die genannten Werte kommen vor") haben die Zeilen unlesbar lang gemacht.
 *
 * Eintraege ohne `key` sind Ueberschriften.
 */
interface OperatorOption {
  key?: string;
  label: string;
  hint?: string;
}

const OPERATOR_OPTIONS: OperatorOption[] = [
  { key: 'equals', label: 'ist gleich', hint: 'Wert stimmt exakt überein (Groß-/Kleinschreibung egal).' },
  { key: 'not_equals', label: 'ist nicht gleich', hint: 'Wert stimmt nicht überein.' },
  { key: 'contains', label: 'enthält', hint: 'Text: Teilstring. Array: mindestens ein Element ist exakt dieser Wert.' },
  { key: 'not_contains', label: 'enthält nicht', hint: 'Umkehrung von "enthält".' },
  { key: 'gt', label: 'größer als', hint: 'Zahlenvergleich.' },
  { key: 'lt', label: 'kleiner als', hint: 'Zahlenvergleich.' },
  { key: 'gte', label: 'größer/gleich', hint: 'Zahlenvergleich.' },
  { key: 'lte', label: 'kleiner/gleich', hint: 'Zahlenvergleich.' },
  { key: 'exists', label: 'existiert', hint: 'Feld ist vorhanden und nicht null. Kein erwarteter Wert nötig.' },
  { key: 'not_exists', label: 'existiert nicht', hint: 'Feld fehlt oder ist null. Kein erwarteter Wert nötig.' },
  { key: 'is_true', label: 'ist true', hint: 'Boolean true (oder der Text "true").' },
  { key: 'is_false', label: 'ist false', hint: 'Boolean false (oder der Text "false").' },

  { label: '── Array ──' },
  { key: 'array_contains_all', label: 'Array enthält', hint: 'Jeder genannte Wert kommt im Array vor. Weitere Einträge sind erlaubt. Der Normalfall — kommagetrennt für mehrere.' },
  { key: 'array_contains_only', label: 'Array enthält ausschließlich', hint: 'Jedes Element des Arrays muss in deiner Liste stehen. Ein einziger zusätzlicher Eintrag im Array lässt die Bedingung scheitern.' },
  { key: 'array_has_extra', label: 'Array hat Extra-Einträge', hint: 'Trifft zu, wenn im Array etwas steht, das NICHT in deiner Liste ist. Gegenstück zu "enthält ausschließlich".' },
  { key: 'array_max_items', label: 'Array max. Einträge', hint: 'Array hat höchstens so viele Elemente. Erwarteter Wert: eine Zahl.' },
  { key: 'array_length_gte', label: 'Anzahl Elemente ≥', hint: 'Array hat mindestens so viele Elemente. Erwarteter Wert: eine Zahl.' },
  { key: 'array_length_eq', label: 'Anzahl Elemente =', hint: 'Array hat genau so viele Elemente. Erwarteter Wert: eine Zahl.' },
  { key: 'array_is_empty', label: 'Array ist leer', hint: 'Array hat keine Elemente (oder fehlt ganz). Kein erwarteter Wert nötig.' },
  { key: 'array_not_empty', label: 'Array nicht leer', hint: 'Array hat mindestens ein Element. Kein erwarteter Wert nötig.' },
  { key: 'array_has_key_value', label: 'Array hat Eintrag (key=value)', hint: 'Mindestens ein Objekt im Array hat diesen Schlüssel mit diesem Wert. Schreibweise: schlüssel=wert' },

  { label: '── Sonstige ──' },
  { key: 'in', label: 'ist einer von', hint: 'Der Feldwert ist einer aus deiner Liste. Kommagetrennt. Gegenstück zu "Array enthält" — hier ist das FELD einzeln, die Liste steht im erwarteten Wert.' },
  { key: 'regex', label: 'matcht Regex', hint: 'Regulärer Ausdruck, ohne Schrägstriche, Groß-/Kleinschreibung egal. Beispiel: ^GTM-' },
  { key: 'has_key', label: 'Objekt hat Schlüssel', hint: 'Das Objekt besitzt diesen Schlüssel — unabhängig vom Wert.' },
];

/**
 * Anzeige im GESCHLOSSENEN Dropdown. Ohne das wuerde MUI die Kinder des gewaehlten
 * MenuItem rendern — samt Tooltip, der dann auch am zugeklappten Feld haengt.
 */
function operatorLabel(key: string): string {
  return OPERATOR_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

/**
 * Baut die MenuItems fuer ein Operator-Dropdown.
 *
 * Der Tooltip sitzt INNERHALB des MenuItem: MUI Select liest `value` von seinen
 * direkten Kindern — ein Tooltip als Wrapper wuerde die Auswahl kaputt machen.
 * Das innere Box fuellt die Zeile, damit die Erklaerung ueberall in der Zeile
 * aufgeht und nicht nur genau ueber dem Text.
 */
function renderOperatorItems() {
  return OPERATOR_OPTIONS.map((op) => {
    if (!op.key) {
      return (
        <MenuItem key={op.label} disabled sx={{ fontSize: 11, color: '#999' }}>
          {op.label}
        </MenuItem>
      );
    }
    return (
      <MenuItem key={op.key} value={op.key} sx={{ py: 0.5 }}>
        <Tooltip title={op.hint ?? ''} placement="right" enterDelay={350} arrow>
          <Box sx={{ width: '100%' }}>{op.label}</Box>
        </Tooltip>
      </MenuItem>
    );
  });
}

/* ── Scan Option ─────────────────────────────────────────── */

interface ScanOption {
  id: string;
  url: string;
  created_at: string;
  engine_name?: string;
  user_name?: string;
}

/* ── Field Path Explorer: Fundorte und Klick auf Werte (ORBITTR-193) ── */

/** Fundort im playwright_raw als JSON-Pointer: Zahl = Array-Index, Text = Schlüssel. */
type PointerSegment = string | number;

/** Woher der angezeigte Wert stammt — eine Stelle oder je Listeneintrag eine (playwright-resolve). */
type ResolvedLocation =
  | { kind: 'node'; pointer: PointerSegment[] }
  | { kind: 'list'; pointers: PointerSegment[][] };

/** Array-Filter im Editor. `at` = Pfad des Arrays, auf das er wirkt (gesetzt beim Klick auf einen Wert). */
interface ArrayFilterDraft {
  id: string;
  field: string;
  operator: string;
  value: string;
  at?: string;
}

/** Antwort von GET /config-plus/playwright-pick. */
interface FieldPathPick {
  field_path: string;
  array_filters: Array<{ field: string; operator: string; value: string; at?: string }>;
  array_filter_logic: Array<'AND' | 'OR'>;
  value: unknown;
  resolved_value: unknown;
  matches: boolean;
  in_list: boolean;
  positional: boolean;
  steps: Array<{ at: string; candidates: number; matching: number; fields: string[] }>;
}

/** Kurzname eines Array-Pfads für Chips und Hinweise: das letzte Segment. */
function arrayScopeLabel(at: string): string {
  return at.split('.').pop() || at;
}

function formatPickValue(value: unknown): string {
  return typeof value === 'string' ? `„${value}“` : JSON.stringify(value);
}

/** Hinweis nach einem Klick: was gesetzt wurde und ob die Auswertung genau den Wert trifft. */
function describePick(pick: FieldPathPick): { severity: 'success' | 'info' | 'warning'; text: string } {
  const byScope = new Map<string, string[]>();
  for (const f of pick.array_filters) {
    const scope = f.at ?? '';
    byScope.set(scope, [...(byScope.get(scope) ?? []), `${f.field} = ${f.value}`]);
  }
  const filterText = [...byScope.entries()]
    .map(([scope, conds]) => `in ${arrayScopeLabel(scope)}: ${conds.join(' UND ')}`)
    .join('; ');
  const positional = pick.positional
    ? ' Achtung: Ein Schritt geht über die Position in einer Liste von Listen — die kann je Scan abweichen.'
    : '';
  if (pick.in_list && pick.matches) {
    return {
      severity: 'info',
      text: `Der Wert steht in einer Liste — der Pfad liefert alle Werte. Regel z. B. mit „Array enthält“ ${formatPickValue(pick.value)}.${filterText ? ` Filter: ${filterText}.` : ''}${positional}`,
    };
  }
  const ambiguous = pick.steps.find((s) => s.matching > 1 && s.fields.length > 0);
  if (!pick.matches) {
    return {
      severity: 'warning',
      text: `Der angeklickte Eintrag lässt sich nicht eindeutig bestimmen${ambiguous ? ` (${ambiguous.matching} Einträge in ${arrayScopeLabel(ambiguous.at)} passen auf den Filter)` : ''} — ausgewertet wird der letzte, dort steht ${formatPickValue(pick.resolved_value)}.${filterText ? ` Filter: ${filterText}.` : ''}${positional}`,
    };
  }
  if (ambiguous) {
    return {
      severity: 'info',
      text: `Pfad und Filter gesetzt (${filterText}). ${ambiguous.matching} Einträge passen, alle mit diesem Wert — ausgewertet wird der letzte.${positional}`,
    };
  }
  return {
    severity: positional ? 'warning' : 'success',
    text: filterText ? `Pfad und Filter gesetzt — ${filterText}.${positional}` : `Pfad gesetzt — kein Array-Filter nötig.${positional}`,
  };
}

/* ── CheckCard Component ─────────────────────────────────── */

function CheckCard({
  check,
  draggableId,
  columnValue,
  isSelected,
  onClick,
}: {
  check: ICheckPlus;
  draggableId: string;
  columnValue: string;
  isSelected: boolean;
  onClick: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: draggableId,
    data: { columnValue },
  });
  return (
    <Paper
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onClick(check.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(check.id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Check ${check.check_code} öffnen`}
      aria-pressed={isSelected}
      sx={{
        p: '12px 14px',
        mb: 1,
        borderRadius: '12px',
        cursor: 'grab',
        border: isSelected ? '2px solid #C8E600' : '2px solid transparent',
        bgcolor: isSelected ? '#fafff0' : '#fff',
        boxShadow: isSelected ? '0 0 0 3px rgba(200,230,0,0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
        opacity: isDragging ? 0.35 : 1,
        transform: CSS.Transform.toString(transform),
        transition: `all 200ms ease, transform ${transition ?? '200ms ease'}`,
        '&:hover': {
          borderColor: '#C8E600',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: `${CSS.Transform.toString(transform) ?? 'translate3d(0,0,0)'} translateY(-2px)`,
        },
      }}
    >
      <Typography
        sx={{ fontSize: 14, fontWeight: 600, color: '#1E1E1E', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={check.title}
      >
        {check.title}
      </Typography>
      <Typography
        sx={{ fontSize: 12, color: '#999', mb: 0.75, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        title={check.description ?? 'Keine Beschreibung'}
      >
        {check.description ?? 'Keine Beschreibung'}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
        <Chip
          label={check.active !== false ? 'Aktiv' : 'Inaktiv'}
          size="small"
          color={check.active !== false ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          size="small"
          label={check.engine_mode === EngineMode.RULE_ENGINE ? '⚡ Rule Engine' : '🔧 Legacy'}
          sx={{ flexShrink: 0, fontWeight: 700, fontSize: 10, height: 20, bgcolor: check.engine_mode === EngineMode.RULE_ENGINE ? '#e8f5e9' : '#fff3e0' }}
        />
      </Box>
    </Paper>
  );
}

/* ── DragPreviewCard ─────────────────────────────────────── */

function DragPreviewCard({ check }: { check: ICheckPlus }) {
  return (
    <Paper
      sx={{
        p: '12px 14px',
        width: 290,
        borderRadius: '12px',
        cursor: 'grabbing',
        border: '2px solid #C8E600',
        bgcolor: '#fff',
        boxShadow: '0 10px 24px rgba(0,0,0,0.2)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, minWidth: 0 }}>
        <Box />
        <Chip
          size="small"
          label={check.engine_mode === EngineMode.RULE_ENGINE ? '⚡ Rule Engine' : '🔧 Legacy'}
          sx={{ flexShrink: 0, fontWeight: 700, fontSize: 10, height: 20, bgcolor: check.engine_mode === EngineMode.RULE_ENGINE ? '#e8f5e9' : '#fff3e0' }}
        />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1E1E1E', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {check.title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#999', mb: 0.75, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {check.description ?? 'Keine Beschreibung'}
      </Typography>
    </Paper>
  );
}

/* ── CategoryColumn (droppable) ──────────────────────────── */

function CategoryColumn({
  columnId,
  title,
  checks,
  selectedCheckId,
  onSelectCheck,
}: {
  columnId: string;
  title: string;
  checks: ICheckPlus[];
  selectedCheckId: string | null;
  onSelectCheck: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId, data: { weight: columnId } });
  const isEmpty = checks.length === 0;
  return (
    <Paper
      ref={setNodeRef}
      role="region"
      aria-label={`Gewichtung ${title}`}
      sx={{
        minWidth: isEmpty ? 240 : 290,
        maxWidth: isEmpty ? 260 : 340,
        p: 1.5,
        bgcolor: isOver ? '#fafff0' : '#fafafa',
        borderRadius: 3,
        border: '1px solid #ececec',
      }}
    >
      <Box sx={{ mb: 1.25, px: 0.75, py: 0.75, borderRadius: 2, bgcolor: '#f2f2f2', borderLeft: '4px solid #C8E600' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E1E1E' }}>{title}</Typography>
        <Typography sx={{ fontSize: 11, color: '#888' }}>{checks.length} Checks</Typography>
      </Box>
      <SortableContext items={checks.map((check) => `${columnId}::${check.id}`)} strategy={verticalListSortingStrategy}>
        {checks.map((check) => (
          <CheckCard
            key={`${columnId}-${check.id}`}
            check={check}
            draggableId={`${columnId}::${check.id}`}
            columnValue={columnId}
            isSelected={selectedCheckId === check.id}
            onClick={onSelectCheck}
          />
        ))}
      </SortableContext>
      <Box sx={{ mt: 1, p: 1, border: '1px dashed #d1d5db', borderRadius: 2, textAlign: 'center', color: 'text.secondary', fontSize: 12, minHeight: isEmpty ? 84 : 'auto', display: 'grid', placeItems: 'center' }}>
        Check hierher ziehen
      </Box>
    </Paper>
  );
}

/* ── Main Page ───────────────────────────────────────────── */

export default function CheckLibraryPage() {
  const { hasRole, user } = useAuth();
  const canEditRules = hasRole([UserRole.DIGA, UserRole.ADMIN]);
  // Der "Entwickler Only"-Switch darf ausschliesslich im Entwicklermodus sichtbar sein
  const devMode = user?.dev_mode === true;

  const [checks, setChecks] = useState<ICheckPlus[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [allLabels, setAllLabels] = useState<ILabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeLabelIds, setActiveLabelIds] = useState<Set<string>>(new Set());
  const labelsInitializedRef = useRef(false);
  const kanbanScrollRef = useRef<HTMLDivElement>(null);
  const [kanbanScroll, setKanbanScroll] = useState({ canLeft: false, canRight: false });
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [activeDragCheckId, setActiveDragCheckId] = useState<string | null>(null);

  // Label management
  const [newLabelName, setNewLabelName] = useState('');
  const [editLabel, setEditLabel] = useState<ILabel | null>(null);
  const [editLabelName, setEditLabelName] = useState('');
  const [deleteLabelTarget, setDeleteLabelTarget] = useState<ILabel | null>(null);

  // Grunddaten draft state (uncommitted edits)
  const [grunddatenDraft, setGrunddatenDraft] = useState<{ title: string; description: string; info_box: string } | null>(null);
  const [grunddatenSaving, setGrunddatenSaving] = useState(false);
  // Delete check confirmation (two-step: 1 = first confirm, 2 = second confirm)
  const [deleteCheckStep, setDeleteCheckStep] = useState<0 | 1 | 2>(0);
  const [deleteCheckSaving, setDeleteCheckSaving] = useState(false);

  // Simulation state
  const [simDialogOpen, setSimDialogOpen] = useState(false);
  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [simCheckId, setSimCheckId] = useState<string | null>(null);
  const [scans, setScans] = useState<ScanOption[]>([]);
  const [selectedScanId, setSelectedScanId] = useState<string>('');
  const [simLoading, setSimLoading] = useState(false);
  const [scanSearchInput, setScanSearchInput] = useState('');
  const [selectedScan, setSelectedScan] = useState<ScanOption | null>(null);
  const [simOutputSticky, setSimOutputSticky] = useState(false);
  const [simStickyExpanded, setSimStickyExpanded] = useState(false);
  const simOutputRef = useRef<HTMLDivElement>(null);
  const dragScrollRafRef = useRef<number | null>(null);
  // Field Path Explorer state (cascading dropdowns)
  const [fieldPathLevels, setFieldPathLevels] = useState<Array<{ keys: Array<{ key: string; hasChildren: boolean; type: string }>; selected: string }>>([]);
  const [fieldPathLoading, setFieldPathLoading] = useState(false);

  // Resolved value from field path exploration
  const [resolvedValue, setResolvedValue] = useState<unknown>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Fundort des aufgelösten Werts — macht die Werte anklickbar
  const [resolvedLocation, setResolvedLocation] = useState<ResolvedLocation | null>(null);
  // Hinweis nach einem Klick auf einen Wert
  const [pickNotice, setPickNotice] = useState<{ severity: 'success' | 'info' | 'warning'; text: string } | null>(null);
  // Popup for expanded resolved value view
  const [resolvedValuePopupOpen, setResolvedValuePopupOpen] = useState(false);
  // Array item filter state (for ambiguous paths through arrays with duplicate keys)
  const [arrayFilters, setArrayFilters] = useState<ArrayFilterDraft[]>([]);
  const [arrayFilterLogic, setArrayFilterLogic] = useState<Array<'AND' | 'OR'>>([]);
  const [dragFilterIdx, setDragFilterIdx] = useState<number | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<{ pos: number; type: 'AND' | 'OR' } | null>(null);
  const [isAmbiguous, setIsAmbiguous] = useState(false);
  const [availableFilterFields, setAvailableFilterFields] = useState<string[]>([]);
  const [filterPanelCollapsed, setFilterPanelCollapsed] = useState(false);

  // Rule definition state — grouped AND/OR structure
  interface RuleConditionDraft {
    id: string;
    operator: string;
    expected_value: string;
  }
  interface RuleGroupDraft {
    id: string;
    rating: 'G' | 'Y' | 'R';
    conditions: RuleConditionDraft[];
  }
  const [ruleGroups, setRuleGroups] = useState<RuleGroupDraft[]>([]);
  const [savingRules, setSavingRules] = useState(false);
  const [autoRedFallback, setAutoRedFallback] = useState(true);
  const [simulationRating, setSimulationRating] = useState<'G' | 'Y' | 'R' | null>(null);
  const [simulationExplanation, setSimulationExplanation] = useState('');
  const [draggedCondition, setDraggedCondition] = useState<{ conditionId: string; fromGroupId: string } | null>(null);
  const [orDropAfterGroupId, setOrDropAfterGroupId] = useState<string | null>(null);
  const [rawDropRating, setRawDropRating] = useState<'G' | 'Y' | 'R' | null>(null);
  const [savingPathOnly, setSavingPathOnly] = useState(false);

  // Saved rules display state
  interface SavedRuleGroup {
    id: string;
    result_rating: 'G' | 'Y' | 'R';
    logic_operator: 'AND' | 'OR';
    field_path: string;
    array_filters: Array<{ field: string; operator: string; value: string; at?: string }> | null;
    array_filter_logic: Array<'AND' | 'OR'> | null;
    rules: Array<{ id: string; operator_key: string; expected_value: string | null }>;
  }
  const [savedRuleGroups, setSavedRuleGroups] = useState<SavedRuleGroup[]>([]);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [rulesDialogCheckId, setRulesDialogCheckId] = useState<string | null>(null);
  const [rulesEditorGroups, setRulesEditorGroups] = useState<RuleGroupDraft[]>([]);
  const [rulesEditorAutoRedFallback, setRulesEditorAutoRedFallback] = useState(true);
  const [rulesEditorFieldPath, setRulesEditorFieldPath] = useState('');
  const [rulesEditorSaving, setRulesEditorSaving] = useState(false);
  const [rulesEditorDraggedCondition, setRulesEditorDraggedCondition] = useState<{ conditionId: string; fromGroupId: string } | null>(null);
  const [rulesEditorOrDropAfterGroupId, setRulesEditorOrDropAfterGroupId] = useState<string | null>(null);
  const [rulesEditorDropRating, setRulesEditorDropRating] = useState<'G' | 'Y' | 'R' | null>(null);
  const [rulesEditorSimLoading, setRulesEditorSimLoading] = useState(false);
  const [rulesEditorSimRating, setRulesEditorSimRating] = useState<'G' | 'Y' | 'R' | null>(null);
  const [rulesEditorSimExplanation, setRulesEditorSimExplanation] = useState('');
  const [rulesEditorSimResolvedValue, setRulesEditorSimResolvedValue] = useState<unknown>(null);
  const [rulesEditorSimResolvedType, setRulesEditorSimResolvedType] = useState<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const [checksRes, catsRes, labelsRes] = await Promise.all([
      api.get<{ checks: ICheckPlus[] }>('/config-plus/checks'),
      api.get<{ categories: ICategory[] }>('/config/categories'),
      api.get<{ labels: ILabel[] }>('/config/labels'),
    ]);
    if (checksRes.success) setChecks(checksRes.data.checks);
    if (catsRes.success) setCategories(catsRes.data.categories);
    if (labelsRes.success) setAllLabels(labelsRes.data.labels);
    if (!checksRes.success) setError(checksRes.error.message);
    else if (!catsRes.success) setError(catsRes.error.message);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Initialize label filter with all labels once on first load
  useEffect(() => {
    if (!labelsInitializedRef.current && allLabels.length > 0) {
      labelsInitializedRef.current = true;
      setActiveLabelIds(new Set(allLabels.map((l) => String(l.id))));
    }
  }, [allLabels]);

  const updateKanbanScroll = useCallback(() => {
    const el = kanbanScrollRef.current;
    if (!el) return;
    setKanbanScroll({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    updateKanbanScroll();
  }, [updateKanbanScroll, activeLabelIds]);

  /* ── Filtering ─────────────────────────────────────────── */

  const filteredChecks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return checks.filter(
      (c) =>
        (showInactive || c.active !== false)
        && (
          c.check_code.toLowerCase().includes(q)
          || c.title.toLowerCase().includes(q)
          || (c.description?.toLowerCase().includes(q) ?? false)
        ),
    );
  }, [checks, search, showInactive]);

  /* ── Grouped data ──────────────────────────────────────── */

  const checksByCategory = useMemo(() => {
    const grouped: Record<string, ICheckPlus[]> = {};
    categories.forEach((cat) => { grouped[cat.id] = []; });
    filteredChecks.forEach((check) => {
      if (grouped[check.category_id]) grouped[check.category_id].push(check);
      else {
        if (!grouped._uncategorized) grouped._uncategorized = [];
        grouped._uncategorized.push(check);
      }
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.check_code.localeCompare(b.check_code));
    });
    return grouped;
  }, [categories, filteredChecks]);

  const checksByLabel = useMemo(() => {
    const grouped: Record<string, ICheckPlus[]> = { no_label: [] };
    allLabels.forEach((label) => { grouped[String(label.id)] = []; });
    filteredChecks.forEach((check) => {
      const labels = check.labels ?? [];
      if (labels.length === 0) {
        grouped.no_label.push(check);
        return;
      }
      labels.forEach((label) => {
        const key = String(label.id);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(check);
      });
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.check_code.localeCompare(b.check_code));
    });
    return grouped;
  }, [allLabels, filteredChecks]);

  /* ── Handlers ──────────────────────────────────────────── */

  // Save Grunddaten draft to backend
  const handleSaveGrunddaten = async () => {
    if (!selectedCheck || !grunddatenDraft) return;
    setGrunddatenSaving(true);
    const fields = [
      { field: 'title', value: grunddatenDraft.title },
      { field: 'description', value: grunddatenDraft.description },
      { field: 'info_box', value: grunddatenDraft.info_box || null },
    ];
    for (const { field, value } of fields) {
      await api.patch(`/config-plus/checks/${selectedCheck.id}`, { [field]: value });
    }
    await loadData(true);
    setGrunddatenDraft(null);
    setGrunddatenSaving(false);
  };

  const handleCancelGrunddaten = () => setGrunddatenDraft(null);

  const handleDeleteCheck = async () => {
    if (!selectedCheck) return;
    setDeleteCheckSaving(true);
    await api.delete(`/config-plus/checks/${selectedCheck.id}`);
    setSelectedCheckId(null);
    setDeleteCheckStep(0);
    setDeleteCheckSaving(false);
    loadData(true);
  };

  const handleFieldUpdate = async (checkId: string, field: string, value: unknown) => {
    setChecks((prev) => prev.map((c) => c.id === checkId ? { ...c, [field]: value } : c));
    const res = await api.patch(`/config-plus/checks/${checkId}`, { [field]: value });
    if (!res.success) loadData(true);
  };

  const handleToggleLabel = async (check: ICheckPlus, labelId: number) => {
    const current = (check.labels ?? []).map((l) => l.id);
    const hasLabel = current.includes(labelId);
    let res;
    if (hasLabel) {
      res = await api.delete(`/config-plus/checks/${check.id}/labels/${labelId}`);
    } else {
      res = await api.post(`/config-plus/checks/${check.id}/labels`, { label_id: labelId });
    }
    if (!res?.success) {
      setError(res?.error?.message ?? 'Label-Zuweisung konnte nicht aktualisiert werden.');
      return;
    }
    loadData(true);
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    const res = await api.post<{ label: ILabel }>('/config/labels', { name: newLabelName.trim() });
    if (res.success) {
      setAllLabels((prev) => [...prev, res.data.label].sort((a, b) => a.name.localeCompare(b.name)));
      setNewLabelName('');
    }
  };

  const handleSaveLabel = async () => {
    if (!editLabel || !editLabelName.trim()) return;
    const res = await api.patch<{ label: ILabel }>(`/config/labels/${editLabel.id}`, { name: editLabelName.trim() });
    if (res.success) {
      setEditLabel(null);
      loadData(true);
    }
  };

  const handleDeleteLabel = async () => {
    if (!deleteLabelTarget) return;
    const res = await api.delete(`/config/labels/${deleteLabelTarget.id}`);
    if (res.success) {
      setAllLabels((prev) => prev.filter((l) => l.id !== deleteLabelTarget.id));
      setDeleteLabelTarget(null);
      loadData(true);
    }
  };

  /* ── Drag & Drop ───────────────────────────────────────── */

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    const checkId = activeId.includes('::') ? activeId.split('::')[1] : activeId;
    setActiveDragCheckId(checkId);
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveDragCheckId(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveDragCheckId(null);
      return;
    }
    const activeId = String(active.id);
    const checkId = activeId.includes('::') ? activeId.split('::')[1] : activeId;
    setActiveDragCheckId(null);
    const rawOverValue = String(over.data.current?.columnValue ?? over.data.current?.weight ?? over.id);
    const overColumnValue = rawOverValue.includes('::') ? rawOverValue.split('::')[0] : rawOverValue;

    // Label column drag
    const targetLabelId = overColumnValue;
    if (targetLabelId === 'no_label') return;
    const check = checks.find((entry) => entry.id === checkId);
    if (!check) return;
    const currentIds = (check.labels ?? []).map((label) => label.id);
    const numericTarget = Number(targetLabelId);
    if (!Number.isFinite(numericTarget)) return;
    if (currentIds.includes(numericTarget)) return;
    await api.post(`/config-plus/checks/${checkId}/labels`, { label_id: String(numericTarget) });
    loadData(true);
  };

  /* ── Simulation ────────────────────────────────────────── */

  const loadFieldPathLevel = async (path: string): Promise<Array<{ key: string; hasChildren: boolean; type: string }>> => {
    const queryParam = path ? `?path=${encodeURIComponent(path)}` : '';
    const res = await api.get<{ path: string; keys: Array<{ key: string; hasChildren: boolean; type: string }> }>(`/config-plus/playwright-structure${queryParam}`);
    if (res.success) return res.data.keys;
    return [];
  };

  const buildFieldPathLevelsFromPath = async (path: string): Promise<Array<{ keys: Array<{ key: string; hasChildren: boolean; type: string }>; selected: string }>> => {
    const parts = path.split('.').filter(Boolean);
    if (parts.length === 0) {
      const root = await loadFieldPathLevel('');
      return root.length > 0 ? [{ keys: root, selected: '' }] : [];
    }

    const levels: Array<{ keys: Array<{ key: string; hasChildren: boolean; type: string }>; selected: string }> = [];
    let prefix = '';

    for (const [idx, part] of parts.entries()) {
      const keys = await loadFieldPathLevel(prefix);
      // Kennen die letzten 20 Scans den Schlüssel nicht (älterer Scan, gespeicherter
      // Pfad, Klick auf einen Wert), bleibt er trotzdem stehen — sonst würde der Pfad
      // hier abgeschnitten und beim Speichern gekürzt.
      const exists = keys.some((k) => k.key === part);
      levels.push({
        keys: exists ? keys : [...keys, { key: part, hasChildren: idx < parts.length - 1, type: 'unknown' }],
        selected: part,
      });
      prefix = prefix ? `${prefix}.${part}` : part;
    }

    if (levels.length === parts.length && levels.length > 0) {
      const lastPart = parts[parts.length - 1];
      const lastInfo = levels[levels.length - 1].keys.find((k) => k.key === lastPart);
      if (lastInfo?.hasChildren) {
        const nextKeys = await loadFieldPathLevel(prefix);
        if (nextKeys.length > 0) {
          levels.push({ keys: nextKeys, selected: '' });
        }
      }
    }

    if (levels.length === 0) {
      const root = await loadFieldPathLevel('');
      return root.length > 0 ? [{ keys: root, selected: '' }] : [];
    }

    return levels;
  };

  const loadAllScans = useCallback(async (): Promise<ScanOption[]> => {
    const res = await api.get<{ scans: Array<{ id: string | number; url: string; created_at: string; scan_template?: { display_name?: string } | null; user?: { name?: string; email?: string } | null }> }>('/scans?limit=1000');
    if (!res.success) return [];
    return (res.data.scans ?? []).map((s) => ({
      id: String(s.id),
      url: s.url,
      created_at: String(s.created_at),
      engine_name: s.scan_template?.display_name ?? undefined,
      user_name: s.user?.name ?? s.user?.email ?? undefined,
    }));
  }, []);

  const openSimDialog = async (checkId: string) => {
    setSimCheckId(checkId);
    setSelectedScanId('');
    setFieldPathLevels([]);
    setResolvedValue(null);
    setResolveError(null);
    setResolvedLocation(null);
    setPickNotice(null);
    setRuleGroups([]);
    // Auto-Rot steht am Check (checks_plus.auto_red_fallback), nicht mehr an einer Fallback-Empfehlung
    setAutoRedFallback(checks.find((c) => c.id === checkId)?.auto_red_fallback ?? true);
    setSimDialogOpen(true);
    try {
      const [allScans, savedRes] = await Promise.all([
        loadAllScans(),
        api.get<{ groups: SavedRuleGroup[] }>(`/config-plus/checks/${checkId}/plus-rule-groups`),
      ]);

      setScans(allScans);
      const firstScan = allScans[0] ?? null;
      if (firstScan) {
        setSelectedScanId(firstScan.id);
        setSelectedScan(firstScan);
        setScanSearchInput(firstScan.url);
      }

      let existingPath = '';
      if (savedRes.success) {
        const groups = savedRes.data.groups ?? [];
        setSavedRuleGroups(groups);
        existingPath = groups[0]?.field_path ?? '';
      }

      const levels = existingPath
        ? await buildFieldPathLevelsFromPath(existingPath)
        : await buildFieldPathLevelsFromPath('');
      if (levels.length > 0) {
        setFieldPathLevels(levels);
      }

      // Auto-simulate if we have a path and scan
      if (existingPath && firstScan) {
        await runSimulation(existingPath, firstScan.id);
      }
    } catch (_e) { /* ignore */ }
  };

  const openPathDialog = async (checkId: string) => {
    setSimCheckId(checkId);
    setSelectedScanId('');
    setFieldPathLevels([]);
    setResolvedValue(null);
    setResolveError(null);
    setResolvedLocation(null);
    setPickNotice(null);
    setPathDialogOpen(true);
    try {
      const [allScans, savedRes] = await Promise.all([
        loadAllScans(),
        api.get<{ groups: SavedRuleGroup[] }>(`/config-plus/checks/${checkId}/plus-rule-groups`),
      ]);

      setScans(allScans);
      const firstScan = allScans[0] ?? null;
      if (firstScan) {
        setSelectedScanId(firstScan.id);
        setSelectedScan(firstScan);
        setScanSearchInput(firstScan.url);
      }

      let existingPath = '';
      if (savedRes.success) {
        const groups = savedRes.data.groups ?? [];
        setSavedRuleGroups(groups);
        existingPath = groups[0]?.field_path ?? '';
      }

      const levels = existingPath
        ? await buildFieldPathLevelsFromPath(existingPath)
        : await buildFieldPathLevelsFromPath('');
      if (levels.length > 0) {
        setFieldPathLevels(levels);
      }

      // Auto-simulate if we have a path and scan
      if (existingPath && firstScan) {
        await runSimulation(existingPath, firstScan.id);
      }
    } catch (_e) { /* ignore */ }
  };

  const handleFieldPathSelect = async (levelIndex: number, value: string) => {
    setPickNotice(null);
    // Update selection at this level and remove all deeper levels
    const updatedLevels = fieldPathLevels.slice(0, levelIndex + 1);
    updatedLevels[levelIndex] = { ...updatedLevels[levelIndex], selected: value };
    setFieldPathLevels(updatedLevels);

    const newPath = updatedLevels.map((l) => l.selected).filter(Boolean).join('.');

    // Check if selected key has children, if so load next level
    const currentLevel = updatedLevels[levelIndex];
    const selectedKeyInfo = currentLevel.keys.find((k) => k.key === value);
    if (selectedKeyInfo?.hasChildren) {
      setFieldPathLoading(true);
      const nextKeys = await loadFieldPathLevel(newPath);
      if (nextKeys.length > 0) {
        setFieldPathLevels([...updatedLevels, { keys: nextKeys, selected: '' }]);
      }
      setFieldPathLoading(false);
    }

    // Auto-simulate whenever a value is selected
    if (value && newPath) {
      setArrayFilters([]);
      setArrayFilterLogic([]);
      setAvailableFilterFields([]);
      setFilterPanelCollapsed(false);
      setIsAmbiguous(false);
      await runSimulation(newPath, undefined, [], []);
    } else {
      setArrayFilters([]);
      setArrayFilterLogic([]);
      setAvailableFilterFields([]);
      setFilterPanelCollapsed(false);
      setIsAmbiguous(false);
      setResolvedValue(null);
      setResolveError(null);
    }
  };

  const computedFieldPath = fieldPathLevels.map((l) => l.selected).filter(Boolean).join('.');

  const runSimulation = async (overridePath?: string, overrideScanId?: string, overrideFilters?: ArrayFilterDraft[], overrideFilterLogic?: Array<'AND' | 'OR'>) => {
    const pathToResolve = overridePath ?? computedFieldPath;
    const scanIdToUse = overrideScanId ?? selectedScanId;
    if (!scanIdToUse || !pathToResolve) return;
    setSimLoading(true);
    setResolvedValue(null);
    setResolveError(null);
    setResolvedLocation(null);
    try {
      const filtersToUse = overrideFilters !== undefined ? overrideFilters : arrayFilters;
      const logicToUse = overrideFilterLogic !== undefined ? overrideFilterLogic : arrayFilterLogic;
      const activeFilters = filtersToUse.filter((f) => f.field && f.value);
      // Build per-condition logic array (length = activeFilters.length - 1)
      const activeLogic = activeFilters.map((_, i) => (logicToUse[i - 1] ?? 'AND')).slice(1) as Array<'AND' | 'OR'>;
      // Logik als Klartext-Liste (AND,OR), nicht als JSON: ["AND"] in der URL sperrt Cloud Armor (SQL-Injection-Regel, 403).
      const filterParam = activeFilters.length > 0
        ? `&filters=${encodeURIComponent(JSON.stringify(activeFilters))}&filterLogic=${encodeURIComponent(activeLogic.join(','))}`
        : '';
      const res = await api.get<{ path: string; resolved_value: unknown; type: string; error?: string; is_ambiguous?: boolean; available_filter_fields?: string[]; location?: ResolvedLocation | null }>(
        `/config-plus/playwright-resolve?scanId=${encodeURIComponent(scanIdToUse)}&path=${encodeURIComponent(pathToResolve)}${filterParam}`,
      );
      if (res.success) {
        if (res.data.error) {
          setResolveError(res.data.error);
        } else {
          setResolvedValue(res.data.resolved_value);
          setResolvedLocation(res.data.location ?? null);
          setIsAmbiguous(res.data.is_ambiguous ?? false);
          if (res.data.available_filter_fields && res.data.available_filter_fields.length > 0) {
            setAvailableFilterFields(res.data.available_filter_fields);
            if (res.data.is_ambiguous) setFilterPanelCollapsed(false); // auto-expand on new ambiguity
            // Auto-create first condition when filter panel appears for the first time
            setArrayFilters((prev) => {
              if (prev.length === 0 && res.data.available_filter_fields && res.data.available_filter_fields.length > 0) {
                return [{ id: crypto.randomUUID(), field: res.data.available_filter_fields[0], operator: 'equals', value: '' }];
              }
              return prev;
            });
          }
          // Auto-collapse after successful filter application
          const filtersToUseLocal = overrideFilters !== undefined ? overrideFilters : arrayFilters;
          if (filtersToUseLocal.filter((f) => f.field && f.value).length > 0 && !res.data.is_ambiguous) {
            setFilterPanelCollapsed(true);
          }
        }
      }
    } catch (e) {
      setResolveError((e as Error).message);
    } finally {
      setSimLoading(false);
    }
  };

  // Navigate into a key from the resolved-value popup
  const handlePopupKeyClick = async (key: string) => {
    const newPath = computedFieldPath ? `${computedFieldPath}.${key}` : key;
    setPickNotice(null);
    setArrayFilters([]);
    setArrayFilterLogic([]);
    setAvailableFilterFields([]);
    setFilterPanelCollapsed(false);
    setIsAmbiguous(false);
    setFieldPathLoading(true);
    try {
      const newLevels = await buildFieldPathLevelsFromPath(newPath);
      if (newLevels.length > 0) setFieldPathLevels(newLevels);
      await runSimulation(newPath, undefined, []);
    } finally {
      setFieldPathLoading(false);
    }
  };

  // Navigate one level back from the resolved-value popup
  const handlePopupNavigateBack = async () => {
    const parts = computedFieldPath.split('.').filter(Boolean);
    setPickNotice(null);
    setArrayFilters([]);
    setArrayFilterLogic([]);
    setAvailableFilterFields([]);
    setFilterPanelCollapsed(false);
    setIsAmbiguous(false);
    if (parts.length <= 1) {
      setFieldPathLoading(true);
      try {
        const root = await buildFieldPathLevelsFromPath('');
        setFieldPathLevels(root);
        setResolvedValue(null);
        setResolveError(null);
      } finally {
        setFieldPathLoading(false);
      }
      return;
    }
    const newPath = parts.slice(0, -1).join('.');
    setFieldPathLoading(true);
    try {
      const newLevels = await buildFieldPathLevelsFromPath(newPath);
      if (newLevels.length > 0) setFieldPathLevels(newLevels);
      await runSimulation(newPath, undefined, []);
    } finally {
      setFieldPathLoading(false);
    }
  };

  // Klick auf einen Wert: der Server leitet aus dem Fundort Pfad + Array-Filter ab und
  // prüft sie gegen die Auswertung; der Explorer übernimmt beides.
  const handleValueClick = async (pointer: PointerSegment[]) => {
    if (!selectedScanId) return;
    setPickNotice(null);
    setFieldPathLoading(true);
    try {
      const res = await api.get<FieldPathPick & { error?: string }>(
        `/config-plus/playwright-pick?scanId=${encodeURIComponent(selectedScanId)}&pointer=${encodeURIComponent(JSON.stringify(pointer))}`,
      );
      if (!res.success) {
        setPickNotice({ severity: 'warning', text: res.error.message });
        return;
      }
      if (res.data.error) {
        setPickNotice({ severity: 'warning', text: res.data.error });
        return;
      }
      const pick = res.data;
      const filters: ArrayFilterDraft[] = pick.array_filters.map((f) => ({ ...f, id: crypto.randomUUID() }));
      const newLevels = await buildFieldPathLevelsFromPath(pick.field_path);
      if (newLevels.length > 0) setFieldPathLevels(newLevels);
      setArrayFilters(filters);
      setArrayFilterLogic(pick.array_filter_logic);
      setAvailableFilterFields([]);
      setIsAmbiguous(false);
      await runSimulation(pick.field_path, undefined, filters, pick.array_filter_logic);
      if (pick.in_list) {
        // Die Auswertung bekommt hier die Liste aller Werte — die zeigen (und simulieren),
        // nicht die Auswahlliste der Einträge, die der Explorer sonst für Mehrdeutiges zeigt.
        setResolvedValue(pick.resolved_value);
        setResolvedLocation(null);
        setIsAmbiguous(false);
        setArrayFilters(filters);
        if (filters.length === 0) setAvailableFilterFields([]);
      }
      setPickNotice(describePick(pick));
    } catch (e) {
      setPickNotice({ severity: 'warning', text: (e as Error).message });
    } finally {
      setFieldPathLoading(false);
    }
  };

  // ── Rule Group Helpers (AND/OR grouping) ───────────────
  const addFirstCondition = (rating: 'G' | 'Y' | 'R') => {
    setRuleGroups((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        rating,
        conditions: [{ id: crypto.randomUUID(), operator: 'equals', expected_value: '' }],
      },
    ]);
  };

  const addConditionToGroup = (groupId: string) => {
    // UND — adds condition to same group
    setRuleGroups((prev) => prev.map((g) =>
      g.id === groupId
        ? { ...g, conditions: [...g.conditions, { id: crypto.randomUUID(), operator: 'equals', expected_value: '' }] }
        : g,
    ));
  };

  const addNewGroupAfter = (groupId: string) => {
    // ODER — creates new group after this one (same rating)
    setRuleGroups((prev) => {
      const idx = prev.findIndex((g) => g.id === groupId);
      if (idx === -1) return prev;
      const rating = prev[idx].rating;
      const newGroup: RuleGroupDraft = {
        id: crypto.randomUUID(),
        rating,
        conditions: [{ id: crypto.randomUUID(), operator: 'equals', expected_value: '' }],
      };
      const next = [...prev];
      next.splice(idx + 1, 0, newGroup);
      return next;
    });
  };

  const updateConditionInGroup = (groupId: string, conditionId: string, field: keyof RuleConditionDraft, value: string) => {
    setRuleGroups((prev) => prev.map((g) =>
      g.id === groupId
        ? { ...g, conditions: g.conditions.map((c) => c.id === conditionId ? { ...c, [field]: value } : c) }
        : g,
    ));
  };

  const removeConditionFromGroup = (groupId: string, conditionId: string) => {
    setRuleGroups((prev) => {
      const updated = prev.map((g) => {
        if (g.id !== groupId) return g;
        return { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) };
      });
      // Remove empty groups
      return updated.filter((g) => g.conditions.length > 0);
    });
  };

  const moveConditionToGroup = (conditionId: string, fromGroupId: string, toGroupId: string) => {
    if (fromGroupId === toGroupId) return;
    setRuleGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      if (!fromGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;
      const updated = prev.map((g) => {
        if (g.id === fromGroupId) return { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) };
        if (g.id === toGroupId) return { ...g, conditions: [...g.conditions, condition] };
        return g;
      });
      return updated.filter((g) => g.conditions.length > 0);
    });
  };

  const moveConditionToNewGroupAfter = (conditionId: string, fromGroupId: string, afterGroupId: string) => {
    setRuleGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      const afterGroup = prev.find((g) => g.id === afterGroupId);
      if (!fromGroup || !afterGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;

      const withoutCondition = prev
        .map((g) => (g.id === fromGroupId ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) } : g))
        .filter((g) => g.conditions.length > 0);

      const insertIdx = withoutCondition.findIndex((g) => g.id === afterGroupId);
      if (insertIdx === -1) return withoutCondition;

      const newGroup: RuleGroupDraft = {
        id: crypto.randomUUID(),
        rating: afterGroup.rating,
        conditions: [condition],
      };
      const next = [...withoutCondition];
      next.splice(insertIdx + 1, 0, newGroup);
      return next;
    });
  };

  const moveConditionToNewGroupInRating = (conditionId: string, fromGroupId: string, targetRating: 'G' | 'Y' | 'R') => {
    setRuleGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      if (!fromGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;

      const withoutCondition = prev
        .map((g) => (g.id === fromGroupId ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) } : g))
        .filter((g) => g.conditions.length > 0);

      let insertAfterIdx = -1;
      withoutCondition.forEach((g, i) => {
        if (g.rating === targetRating) insertAfterIdx = i;
      });

      const newGroup: RuleGroupDraft = {
        id: crypto.randomUUID(),
        rating: targetRating,
        conditions: [condition],
      };

      const next = [...withoutCondition];
      next.splice(insertAfterIdx + 1, 0, newGroup);
      return next;
    });
  };

  /**
   * Was gehoert ins Feld "Erwarteter Wert"? Haengt am Operator und ist die haeufigste
   * Fehlerquelle: eine Liste da eintragen, wo eine Zahl erwartet wird (oder umgekehrt),
   * sieht aus wie "Regel trifft nicht zu".
   */
  const operatorHelperText = (operator: string): string | undefined => {
    if (['array_contains_all', 'array_contains_only', 'array_has_extra', 'in'].includes(operator)) {
      return 'Kommagetrennt: a, b, c';
    }
    if (['array_max_items', 'array_length_gte', 'array_length_eq'].includes(operator)) {
      return 'Eine Zahl';
    }
    if (operator === 'regex') return 'Regulärer Ausdruck, ohne Schrägstriche';
    if (['array_is_empty', 'array_not_empty', 'exists', 'not_exists', 'is_true', 'is_false'].includes(operator)) {
      return 'Kein Wert nötig';
    }
    return undefined;
  };

  /* ── Rule Simulation ───────────────────────────────────── */

  /**
   * Die Simulation nutzt exakt dieselben Operator-Implementierungen wie der echte
   * Scan (src/shared/ruleOperators.ts). Vorher lag hier eine zweite Kopie, der
   * sechs Operatoren fehlten — die meldeten in der Vorschau still "kein Match",
   * obwohl der Scan gematcht haette.
   *
   * `null` = Operator unbekannt. Das wird sichtbar gemacht statt als "trifft nicht zu"
   * verschluckt, sonst sucht man den Fehler wieder in der Regel statt im Operator.
   */
  const evaluateCondition = (actualValue: unknown, operator: string, expectedValue: string): boolean | null =>
    applyOperator(operator, actualValue, expectedValue);

  const evaluateRuleGroupsForValue = (
    currentValue: unknown,
    groups: RuleGroupDraft[],
    autoRedFallbackEnabled: boolean,
  ): { rating: 'G' | 'Y' | 'R' | null; explanation: string } => {
    const greenGroups = groups.filter((g) => g.rating === 'G');
    const yellowGroups = groups.filter((g) => g.rating === 'Y');
    const redGroups = groups.filter((g) => g.rating === 'R');

    // Evaluate a group: all conditions in a group are AND-connected
    const unknownOperators = new Set<string>();
    const evaluateGroup = (group: RuleGroupDraft): boolean => {
      return group.conditions.every((c) => {
        const res = evaluateCondition(currentValue, c.operator, c.expected_value);
        if (res === null) { unknownOperators.add(c.operator); return false; }
        return res;
      });
    };

    // Groups within a rating are OR-connected — at least one group must pass
    const greenMatch = greenGroups.length > 0 && greenGroups.some(evaluateGroup);
    const yellowMatch = yellowGroups.length > 0 && yellowGroups.some(evaluateGroup);
    const redMatch = redGroups.length > 0 && redGroups.some(evaluateGroup);

    // Evaluation priority: Green > Yellow > Red (best / most specific rule wins)
    // Green always beats Yellow — even if Yellow's conditions are a subset of Green's.
    if (greenMatch) {
      return { rating: 'G', explanation: '🟢 Grün — Grün-Bedingungsgruppe erfüllt.' };
    }
    if (yellowMatch) {
      return { rating: 'Y', explanation: '🟡 Gelb — Gelb-Bedingungsgruppe zutreffend.' };
    }
    if (redMatch) {
      return { rating: 'R', explanation: '🔴 Rot — Explizite Rot-Bedingungsgruppe erfüllt.' };
    }
    if (autoRedFallbackEnabled) {
      return { rating: 'R', explanation: '🔴 Rot (Fallback) — Weder Grün noch Gelb matcht → automatisch Rot.' };
    }
    if (unknownOperators.size > 0) {
      return {
        rating: null,
        explanation: `⚠️ Unbekannter Operator: ${[...unknownOperators].join(', ')} — die Simulation kann diese Bedingung nicht auswerten.`,
      };
    }
    return { rating: null, explanation: '⚪ Kein Match — Keine Regel trifft zu und kein Fallback aktiv.' };
  };

  const runRuleSimulation = () => {
    if (resolvedValue === null) return;
    const result = evaluateRuleGroupsForValue(resolvedValue, ruleGroups, autoRedFallback);
    setSimulationRating(result.rating);
    setSimulationExplanation(result.explanation);
  };

  const runRulesEditorSimulation = async () => {
    if (!selectedScanId || !rulesEditorFieldPath) {
      setRulesEditorSimRating(null);
      setRulesEditorSimExplanation('Bitte zuerst einen Scan auswählen und sicherstellen, dass ein Feldpfad vorhanden ist.');
      setRulesEditorSimResolvedValue(null);
      setRulesEditorSimResolvedType('');
      return;
    }
    setRulesEditorSimLoading(true);
    setRulesEditorSimRating(null);
    setRulesEditorSimExplanation('');
    try {
      // Pass saved array filters so resolution is identical to what the rule engine will do
      const savedFilters = savedRuleGroups.length > 0 ? (savedRuleGroups[0].array_filters ?? []) : [];
      const savedFilterLogic = savedRuleGroups.length > 0 ? (savedRuleGroups[0].array_filter_logic ?? []) : [];
      // Je Lücke ein Eintrag, fehlende wie in der Auswertung AND — als Klartext-Liste, siehe runSimulation
      const savedLogicList = savedFilters.map((_, i) => savedFilterLogic[i - 1] ?? 'AND').slice(1);
      const filterParam = savedFilters.length > 0
        ? `&filters=${encodeURIComponent(JSON.stringify(savedFilters))}&filterLogic=${encodeURIComponent(savedLogicList.join(','))}`
        : '';
      const res = await api.get<{ path: string; resolved_value: unknown; type: string; error?: string }>(
        `/config-plus/playwright-resolve?scanId=${encodeURIComponent(selectedScanId)}&path=${encodeURIComponent(rulesEditorFieldPath)}${filterParam}`,
      );
      if (!res.success) {
        setRulesEditorSimRating(null);
        setRulesEditorSimExplanation(res.error?.message ?? 'Simulation konnte nicht ausgeführt werden.');
        setRulesEditorSimResolvedValue(null);
        setRulesEditorSimResolvedType('');
        return;
      }
      if (res.data.error) {
        setRulesEditorSimRating(null);
        setRulesEditorSimExplanation(res.data.error ?? 'Simulation konnte nicht ausgeführt werden.');
        setRulesEditorSimResolvedValue(null);
        setRulesEditorSimResolvedType('');
        return;
      }
      const result = evaluateRuleGroupsForValue(res.data.resolved_value, rulesEditorGroups, rulesEditorAutoRedFallback);
      setRulesEditorSimRating(result.rating);
      setRulesEditorSimExplanation(result.explanation);
      setRulesEditorSimResolvedValue(res.data.resolved_value);
      setRulesEditorSimResolvedType(res.data.type);
    } catch (e) {
      setRulesEditorSimRating(null);
      setRulesEditorSimExplanation((e as Error).message);
      setRulesEditorSimResolvedValue(null);
      setRulesEditorSimResolvedType('');
    } finally {
      setRulesEditorSimLoading(false);
    }
  };

  const saveRuleConditions = async () => {
    if (!simCheckId || !computedFieldPath || ruleGroups.length === 0) return;
    setSavingRules(true);
    try {
      // Build groups from the new grouped structure
      const groups: Array<{ rating: string; logic_operator: string; rules: Array<{ operator_key: string; expected_value: string | null }> }> = [];
      for (const group of ruleGroups) {
        groups.push({
          rating: group.rating,
          logic_operator: 'AND', // conditions within a group are always AND
          rules: group.conditions.map((c) => ({ operator_key: c.operator, expected_value: c.expected_value || null })),
        });
      }

      await api.post(`/config-plus/checks/${simCheckId}/plus-rules-save`, {
        field_path: computedFieldPath,
        auto_red_fallback: autoRedFallback,
        groups,
        array_filters: arrayFilters.length > 0 ? arrayFilters : null,
        array_filter_logic: arrayFilterLogic.length > 0 ? arrayFilterLogic : null,
      });

      setRuleGroups([]);
      loadData(true);
      loadSavedRules(simCheckId);
      setSimDialogOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingRules(false);
    }
  };

  const saveFieldPathAdjustment = async (openRulesAfterSave: boolean) => {
    if (!simCheckId || !computedFieldPath) return;
    if (savedRuleGroups.length === 0) {
      setError('Pfad kann nur gespeichert werden, wenn bereits Regelgruppen existieren. Bitte zuerst Regeln anlegen.');
      return;
    }

    setSavingPathOnly(true);
    try {
      const groups = savedRuleGroups.map((group) => ({
        rating: group.result_rating,
        logic_operator: group.logic_operator,
        rules: group.rules.map((rule) => ({
          operator_key: rule.operator_key,
          expected_value: rule.expected_value,
        })),
      }));

      await api.post(`/config-plus/checks/${simCheckId}/plus-rules-save`, {
        field_path: computedFieldPath,
        // Bis 11/2026 hing das an "gibt es eine Fallback-Empfehlung" — ohne Text schaltete
        // das Speichern des Pfads Auto-Rot still ab. Massgeblich ist das Feld am Check.
        auto_red_fallback: checks.find((c) => c.id === simCheckId)?.auto_red_fallback ?? true,
        groups,
        array_filters: arrayFilters.length > 0 ? arrayFilters : null,
        array_filter_logic: arrayFilterLogic.length > 0 ? arrayFilterLogic : null,
      });

      await loadSavedRules(simCheckId);
      setPathDialogOpen(false);
      if (openRulesAfterSave) {
        openRulesDialog(simCheckId);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingPathOnly(false);
    }
  };

  const loadSavedRules = async (checkId: string): Promise<{ groups: SavedRuleGroup[] } | null> => {
    try {
      const res = await api.get<{ groups: SavedRuleGroup[] }>(
        `/config-plus/checks/${checkId}/plus-rule-groups`,
      );
      if (res.success) {
        const groups = res.data.groups ?? [];
        setSavedRuleGroups(groups);
        // Restore array filters from the first rule group (all groups share the same filters).
        // Ohne gespeicherte Filter leeren — sonst wandern die des zuletzt geöffneten Checks mit.
        setArrayFilters((groups[0]?.array_filters ?? []).map((f) => ({ ...f, id: crypto.randomUUID() })));
        setArrayFilterLogic(groups[0]?.array_filter_logic ?? []);
        return { groups };
      }
    } catch (_e) { /* ignore */ }
    return null;
  };

  const hydrateRulesEditor = (groups: SavedRuleGroup[], autoRedFallbackEnabled: boolean) => {
    const nextGroups: RuleGroupDraft[] = groups.map((group) => {
      return {
        id: crypto.randomUUID(),
        rating: group.result_rating,
        conditions: group.rules.map((rule) => ({
          id: crypto.randomUUID(),
          operator: rule.operator_key,
          expected_value: rule.expected_value ?? '',
        })),
      };
    });

    setRulesEditorGroups(nextGroups);
    setRulesEditorFieldPath(groups[0]?.field_path ?? '');
    setRulesEditorAutoRedFallback(autoRedFallbackEnabled);
  };

  const addRulesEditorFirstCondition = (rating: 'G' | 'Y' | 'R') => {
    setRulesEditorGroups((prev) => [
      ...prev,
      { id: crypto.randomUUID(), rating, conditions: [{ id: crypto.randomUUID(), operator: 'equals', expected_value: '' }] },
    ]);
  };

  const addRulesEditorConditionToGroup = (groupId: string) => {
    setRulesEditorGroups((prev) => prev.map((g) => (
      g.id === groupId
        ? { ...g, conditions: [...g.conditions, { id: crypto.randomUUID(), operator: 'equals', expected_value: '' }] }
        : g
    )));
  };

  const addRulesEditorGroupAfter = (groupId: string) => {
    setRulesEditorGroups((prev) => {
      const idx = prev.findIndex((g) => g.id === groupId);
      if (idx === -1) return prev;
      const rating = prev[idx].rating;
      const next = [...prev];
      next.splice(idx + 1, 0, {
        id: crypto.randomUUID(),
        rating,
        conditions: [{ id: crypto.randomUUID(), operator: 'equals', expected_value: '' }],
      });
      return next;
    });
  };

  const updateRulesEditorCondition = (groupId: string, conditionId: string, field: keyof RuleConditionDraft, value: string) => {
    setRulesEditorGroups((prev) => prev.map((g) => (
      g.id === groupId
        ? { ...g, conditions: g.conditions.map((c) => (c.id === conditionId ? { ...c, [field]: value } : c)) }
        : g
    )));
  };

  const removeRulesEditorCondition = (groupId: string, conditionId: string) => {
    setRulesEditorGroups((prev) => prev
      .map((g) => (g.id === groupId ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) } : g))
      .filter((g) => g.conditions.length > 0));
  };

  const moveRulesEditorCondition = (conditionId: string, fromGroupId: string, toGroupId: string) => {
    if (fromGroupId === toGroupId) return;
    setRulesEditorGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      if (!fromGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;
      return prev
        .map((g) => {
          if (g.id === fromGroupId) return { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) };
          if (g.id === toGroupId) return { ...g, conditions: [...g.conditions, condition] };
          return g;
        })
        .filter((g) => g.conditions.length > 0);
    });
  };

  const moveRulesEditorConditionToNewGroupAfter = (conditionId: string, fromGroupId: string, afterGroupId: string) => {
    setRulesEditorGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      const afterGroup = prev.find((g) => g.id === afterGroupId);
      if (!fromGroup || !afterGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;

      const withoutCondition = prev
        .map((g) => (g.id === fromGroupId ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) } : g))
        .filter((g) => g.conditions.length > 0);

      const insertIdx = withoutCondition.findIndex((g) => g.id === afterGroupId);
      if (insertIdx === -1) return withoutCondition;

      const newGroup: RuleGroupDraft = {
        id: crypto.randomUUID(),
        rating: afterGroup.rating,
        conditions: [condition],
      };
      const next = [...withoutCondition];
      next.splice(insertIdx + 1, 0, newGroup);
      return next;
    });
  };

  const moveRulesEditorConditionToNewGroupInRating = (
    conditionId: string,
    fromGroupId: string,
    targetRating: 'G' | 'Y' | 'R',
  ) => {
    setRulesEditorGroups((prev) => {
      const fromGroup = prev.find((g) => g.id === fromGroupId);
      if (!fromGroup) return prev;
      const condition = fromGroup.conditions.find((c) => c.id === conditionId);
      if (!condition) return prev;

      const withoutCondition = prev
        .map((g) => (g.id === fromGroupId ? { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) } : g))
        .filter((g) => g.conditions.length > 0);

      let insertAfterIdx = -1;
      withoutCondition.forEach((g, i) => {
        if (g.rating === targetRating) insertAfterIdx = i;
      });

      const newGroup: RuleGroupDraft = {
        id: crypto.randomUUID(),
        rating: targetRating,
        conditions: [condition],
      };

      const next = [...withoutCondition];
      next.splice(insertAfterIdx + 1, 0, newGroup);
      return next;
    });
  };

  const saveRulesDialogChanges = async () => {
    if (!rulesDialogCheckId || !rulesEditorFieldPath || rulesEditorGroups.length === 0) return;
    setRulesEditorSaving(true);
    try {
      const groups = rulesEditorGroups.map((group) => ({
        rating: group.rating,
        logic_operator: 'AND',
        rules: group.conditions.map((cond) => ({
          operator_key: cond.operator,
          expected_value: cond.expected_value || null,
        })),
      }));

      await api.post(`/config-plus/checks/${rulesDialogCheckId}/plus-rules-save`, {
        field_path: rulesEditorFieldPath,
        auto_red_fallback: rulesEditorAutoRedFallback,
        groups,
        array_filters: arrayFilters.length > 0 ? arrayFilters : null,
        array_filter_logic: arrayFilterLogic.length > 0 ? arrayFilterLogic : null,
      });

      const refreshed = await loadSavedRules(rulesDialogCheckId);
      if (refreshed) {
        hydrateRulesEditor(refreshed.groups, rulesEditorAutoRedFallback);
      }
      loadData(true);
      setRulesDialogOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRulesEditorSaving(false);
    }
  };

  const openRulesDialog = async (checkId: string) => {
    setRulesDialogCheckId(checkId);
    setRulesDialogOpen(true);
    setRulesEditorSimRating(null);
    setRulesEditorSimExplanation('');
    if (scans.length === 0) {
      const allScans = await loadAllScans();
      setScans(allScans);
      if (!selectedScanId && allScans.length > 0) {
        setSelectedScanId(allScans[0].id);
        setSelectedScan(allScans[0]);
        setScanSearchInput(allScans[0].url);
      }
    }
    const data = await loadSavedRules(checkId);
    if (data) {
      hydrateRulesEditor(data.groups, checks.find((c) => c.id === checkId)?.auto_red_fallback ?? true);
    }
  };

  const closeRulesDialog = () => {
    setRulesDialogOpen(false);
    setRulesDialogCheckId(null);
  };

  useEffect(() => {
    if (!rulesDialogOpen) return;
    if (!selectedScanId || !rulesEditorFieldPath || rulesEditorGroups.length === 0) return;
    void runRulesEditorSimulation();
  }, [
    rulesDialogOpen,
    selectedScanId,
    rulesEditorFieldPath,
    rulesEditorGroups,
    rulesEditorAutoRedFallback,
  ]);

  // Sticky detection via IntersectionObserver — fires when simulation output Paper scrolls out of view
  useEffect(() => {
    if (!rulesDialogOpen) { setSimOutputSticky(false); setSimStickyExpanded(false); return; }
    let observer: IntersectionObserver | null = null;
    // Small delay so the dialog DOM is fully mounted before we query refs
    const timer = setTimeout(() => {
      const el = simOutputRef.current;
      if (!el) return;
      // Find the nearest scrollable ancestor (MUI DialogContent uses overflow:auto)
      const scrollRoot = el.closest('[class*="MuiDialogContent"]') as HTMLElement | null
        ?? el.closest('[style*="overflow"]') as HTMLElement | null;
      if (!scrollRoot) return;
      observer = new IntersectionObserver(
        ([entry]) => { setSimOutputSticky(!entry.isIntersecting); },
        { root: scrollRoot, threshold: 0 },
      );
      observer.observe(el);
    }, 50);
    return () => { clearTimeout(timer); observer?.disconnect(); };
  }, [rulesDialogOpen]);

  // Auto-scroll the DialogContent while dragging conditions near the top/bottom edge
  useEffect(() => {
    const isDragging = draggedCondition !== null || rulesEditorDraggedCondition !== null;
    if (!isDragging) {
      if (dragScrollRafRef.current !== null) {
        cancelAnimationFrame(dragScrollRafRef.current);
        dragScrollRafRef.current = null;
      }
      return;
    }

    let clientY = 0;

    const findScrollContainer = (): HTMLElement | null => {
      // Try to find the open MUI DialogContent — the one that is currently scrollable
      const containers = document.querySelectorAll('[class*="MuiDialogContent"]');
      for (const el of containers) {
        const style = window.getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return el as HTMLElement;
        }
      }
      return null;
    };

    const ZONE = 80;   // px from edge to trigger scroll
    const MAX_SPEED = 18; // px per frame at edge

    const scroll = () => {
      const container = findScrollContainer();
      if (container) {
        const rect = container.getBoundingClientRect();
        const distTop = clientY - rect.top;
        const distBottom = rect.bottom - clientY;

        if (distTop < ZONE && distTop > 0) {
          const speed = Math.ceil(MAX_SPEED * (1 - distTop / ZONE));
          container.scrollTop -= speed;
        } else if (distBottom < ZONE && distBottom > 0) {
          const speed = Math.ceil(MAX_SPEED * (1 - distBottom / ZONE));
          container.scrollTop += speed;
        }
      }
      dragScrollRafRef.current = requestAnimationFrame(scroll);
    };

    const onDragOver = (e: DragEvent) => { clientY = e.clientY; };

    document.addEventListener('dragover', onDragOver);
    dragScrollRafRef.current = requestAnimationFrame(scroll);

    return () => {
      document.removeEventListener('dragover', onDragOver);
      if (dragScrollRafRef.current !== null) {
        cancelAnimationFrame(dragScrollRafRef.current);
        dragScrollRafRef.current = null;
      }
    };
  }, [draggedCondition, rulesEditorDraggedCondition]);

  /* ── Derived ───────────────────────────────────────────── */

  const selectedCheck = checks.find((check) => check.id === selectedCheckId) ?? null;
  const selectedCategory = categories.find((cat) => cat.id === selectedCheck?.category_id);
  const activeDragCheck = checks.find((check) => check.id === activeDragCheckId) ?? null;

  // Load saved rules when selecting a check; reset Grunddaten draft
  useEffect(() => {
    setGrunddatenDraft(null);
    if (selectedCheckId) {
      loadSavedRules(selectedCheckId);
    } else {
      setSavedRuleGroups([]);
    }
  }, [selectedCheckId]);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const slugifyCheck = (s: string) =>
    s.toLowerCase()
      .replace(/[\s\-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 16);  // varchar(20) — keep room for _99 suffix
  const [newCheck, setNewCheck] = useState<{ check_code: string; title: string; description: string; label_ids: number[] }>({ check_code: '', title: '', description: '', label_ids: [] });
  const [createLoading, setCreateLoading] = useState(false);

  const handleCreateCheck = async () => {
    if (!newCheck.title.trim()) return;
    setCreateLoading(true);
    const { label_ids, ...checkData } = newCheck;
    const res = await api.post<ICheckPlus>('/config-plus/checks', { ...checkData, category_id: categories[0]?.id ?? '' });
    if (res.success) {
      for (const labelId of label_ids) {
        await api.post(`/config-plus/checks/${res.data.id}/labels`, { label_id: labelId });
      }
      setCreateDialogOpen(false);
      setNewCheck({ check_code: '', title: '', description: '', label_ids: [] });
      loadData(true);
      openSimDialog(res.data.id);
    } else {
      setError(res.error?.message ?? 'Fehler beim Erstellen');
    }
    setCreateLoading(false);
  };

  // ── Array Item Filter Panel ───────────────────────────
  // Shown when server detects the same key in multiple array items
  // ── Shared MUI overrides for dark-bg selects / text fields ───
  const darkSelectSx = {
    color: '#90caf9',
    '.MuiOutlinedInput-notchedOutline': { borderColor: '#1565c0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#42a5f5' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#42a5f5' },
    '.MuiSvgIcon-root': { color: '#90caf9' },
  };
  const darkLabelSx = { color: '#90caf9', '&.Mui-focused': { color: '#42a5f5' } };
  const darkTextFieldSx = {
    '& .MuiInputLabel-root': { color: '#90caf9' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#42a5f5' },
    '& .MuiOutlinedInput-root': {
      color: '#90caf9',
      '& fieldset': { borderColor: '#1565c0' },
      '&:hover fieldset': { borderColor: '#42a5f5' },
      '&.Mui-focused fieldset': { borderColor: '#42a5f5' },
    },
  };

  // ── Filter groups: consecutive AND conditions = one group, OR = group boundary ──
  type ArrayFilterCond = ArrayFilterDraft;
  type GroupedCond = ArrayFilterCond & { globalIdx: number };
  const filterGroups: GroupedCond[][] = (() => {
    if (arrayFilters.length === 0) return [];
    const groups: GroupedCond[][] = [];
    let cur: GroupedCond[] = [{ ...arrayFilters[0], globalIdx: 0 }];
    for (let gi = 1; gi < arrayFilters.length; gi++) {
      if ((arrayFilterLogic[gi - 1] ?? 'AND') === 'AND') {
        cur.push({ ...arrayFilters[gi], globalIdx: gi });
      } else {
        groups.push(cur);
        cur = [{ ...arrayFilters[gi], globalIdx: gi }];
      }
    }
    groups.push(cur);
    return groups;
  })();

  const addCondToGroup = (groupIdx: number) => {
    const group = filterGroups[groupIdx];
    const insertAt = group[group.length - 1].globalIdx + 1;
    // Eine UND-Bedingung gilt für dasselbe Array wie ihre Gruppe
    const newCond: ArrayFilterCond = { id: crypto.randomUUID(), field: availableFilterFields[0] ?? '', operator: 'equals', value: '', at: group[0].at };
    setArrayFilters((prev) => { const next = [...prev]; next.splice(insertAt, 0, newCond); return next; });
    setArrayFilterLogic((prev) => { const next = [...prev]; next.splice(insertAt - 1, 0, 'AND'); return next; });
  };

  const handleFilterDrop = (insertBefore: number, logic: 'AND' | 'OR') => {
    const src = dragFilterIdx;
    setDragFilterIdx(null);
    setDragOverTarget(null);
    // Only true no-op: dropping on itself
    if (src === null || src === insertBefore) return;
    const srcCond = arrayFilters[src];
    const filtersAfterRemove = arrayFilters.filter((_, i) => i !== src);
    const logicAfterRemove = (() => {
      const result = [...arrayFilterLogic];
      if (result.length > 0) result.splice(src === 0 ? 0 : src - 1, 1);
      return result;
    })();
    const adj = insertBefore > src ? insertBefore - 1 : insertBefore;
    const newFilters = [...filtersAfterRemove];
    newFilters.splice(adj, 0, srcCond);
    const newLogic = [...logicAfterRemove];
    if (filtersAfterRemove.length > 0) newLogic.splice(Math.max(0, adj - 1), 0, logic);
    setArrayFilters(newFilters);
    setArrayFilterLogic(newLogic);
  };

  const renderFilterCondRow = (cond: GroupedCond) => (
    <Box
      key={cond.id}
      draggable
      onDragStart={() => { setTimeout(() => setDragFilterIdx(cond.globalIdx), 0); setDragOverTarget(null); }}
      onDragEnd={() => { setDragFilterIdx(null); setDragOverTarget(null); }}
      sx={{
        display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
        p: 0.75, borderRadius: 1,
        bgcolor: dragFilterIdx === cond.globalIdx ? 'rgba(21,101,192,0.18)' : 'transparent',
        border: dragFilterIdx === cond.globalIdx ? '1px dashed #1565c0' : '1px solid transparent',
        cursor: 'grab', '&:active': { cursor: 'grabbing' },
        opacity: dragFilterIdx === cond.globalIdx ? 0.5 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      <Box sx={{ color: '#1565c0', fontSize: 16, lineHeight: 1, userSelect: 'none', px: 0.25 }} title="Ziehen zum Verschieben">⠿</Box>
      {cond.at !== undefined && (
        <Tooltip title={`Gilt nur für die Einträge in ${cond.at || 'der Wurzel'}`} arrow>
          <Chip label={arrayScopeLabel(cond.at)} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#0d47a1', color: '#bbdefb', fontFamily: 'monospace' }} />
        </Tooltip>
      )}
      <FormControl size="small" sx={{ flex: '1 1 110px' }}>
        <InputLabel sx={darkLabelSx}>Feld</InputLabel>
        <Select value={cond.field} label="Feld" sx={darkSelectSx}
          MenuProps={{ PaperProps: { sx: { bgcolor: '#0d1117', color: '#90caf9' } } }}
          onChange={(e) => setArrayFilters((prev) => prev.map((f) => f.id === cond.id ? { ...f, field: e.target.value } : f))}>
          {/* Ein per Klick gesetztes Feld (z. B. value.event) steht nicht in der Schlüsselliste — trotzdem zeigen */}
          {[...new Set([...availableFilterFields, cond.field].filter(Boolean))].map((f) => <MenuItem key={f} value={f} sx={{ color: '#90caf9' }}>{f}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ flex: '1 1 130px' }}>
        <InputLabel sx={darkLabelSx}>Operator</InputLabel>
        <Select value={cond.operator} label="Operator" sx={darkSelectSx}
          MenuProps={{ PaperProps: { sx: { bgcolor: '#0d1117', color: '#90caf9', maxHeight: OPERATOR_MENU_MAX_HEIGHT } } }}
          onChange={(e) => setArrayFilters((prev) => prev.map((f) => f.id === cond.id ? { ...f, operator: e.target.value } : f))}>
          <MenuItem value="equals" sx={{ color: '#90caf9' }}>ist gleich</MenuItem>
          <MenuItem value="not_equals" sx={{ color: '#90caf9' }}>ist nicht</MenuItem>
          <MenuItem value="contains" sx={{ color: '#90caf9' }}>enthält</MenuItem>
          <MenuItem value="not_contains" sx={{ color: '#90caf9' }}>enthält nicht</MenuItem>
        </Select>
      </FormControl>
      <TextField size="small" label="Wert" value={cond.value}
        onChange={(e) => setArrayFilters((prev) => prev.map((f) => f.id === cond.id ? { ...f, value: e.target.value } : f))}
        sx={{ flex: '1 1 140px', ...darkTextFieldSx }} />
      <IconButton size="small" onClick={() => {
        setArrayFilters((prev) => prev.filter((f) => f.id !== cond.id));
        setArrayFilterLogic((prev) => { const next = [...prev]; next.splice(cond.globalIdx === 0 ? 0 : cond.globalIdx - 1, 1); return next; });
      }}>
        <DeleteOutlineIcon fontSize="small" sx={{ color: '#90caf9' }} />
      </IconButton>
    </Box>
  );

  // ── UND drop zone — hidden (height 0) when not dragging, visible (height 36px) during drag ──
  // setTimeout in onDragStart defers the state update so the DnD ghost is captured BEFORE
  // layout changes — this makes ALL items draggable, not just the first.
  const renderFilterAndZone = (pos: number) => {
    const src = dragFilterIdx;
    const isDragging = src !== null;
    const isNoop = src !== null && (pos === src || pos === src + 1);
    const isVisible = isDragging && !isNoop;
    const isActive = isVisible && dragOverTarget?.pos === pos && dragOverTarget.type === 'AND';
    return (
      <Box
        key={`dz-and-${pos}`}
        onDragOver={(e) => { if (isVisible) { e.preventDefault(); setDragOverTarget({ pos, type: 'AND' }); } }}
        onDragLeave={(e) => {
          if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node) && dragOverTarget?.pos === pos && dragOverTarget.type === 'AND')
            setDragOverTarget(null);
        }}
        onDrop={(e) => { if (isVisible) { e.preventDefault(); handleFilterDrop(pos, 'AND'); } }}
        sx={{
          height: isVisible ? 36 : 0,
          overflow: 'hidden',
          borderRadius: 1,
          my: isVisible ? 0.25 : 0,
          border: isActive ? '2px dashed #42a5f5' : '1px dashed rgba(66,165,245,0.4)',
          bgcolor: isActive ? 'rgba(66,165,245,0.12)' : 'rgba(66,165,245,0.03)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'height 0.12s ease, border-color 0.1s, background-color 0.1s',
        }}
      >
        <Typography variant="caption" sx={{ color: isActive ? '#42a5f5' : 'rgba(66,165,245,0.5)', fontWeight: 700, fontSize: 10, userSelect: 'none' }}>
          + UND hier einfügen
        </Typography>
      </Box>
    );
  };

  const arrayFilterPanel: React.ReactNode = availableFilterFields.length > 0 ? (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: '#1565c0', bgcolor: '#010d1a' }}>
      {/* Collapsible header */}
      <Box
        onClick={() => setFilterPanelCollapsed((v) => !v)}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: filterPanelCollapsed ? 0 : 1.5 }}
      >
        <Typography variant="body2" sx={{ color: '#90caf9', fontWeight: 700, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isAmbiguous ? '⚠' : '✓'} Array-Filter
          {!isAmbiguous && arrayFilters.some((f) => f.field && f.value) && (
            <Chip label="aktiv" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#1565c0', color: '#fff', ml: 0.5 }} />
          )}
          {isAmbiguous && (
            <Chip label="mehrdeutig" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#b71c1c', color: '#fff', ml: 0.5 }} />
          )}
        </Typography>
        <IconButton size="small" sx={{ color: '#90caf9', p: 0.25 }}>
          {filterPanelCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Box>

      {!filterPanelCollapsed && (
        <>
          {filterGroups.map((group, groupIdx) => (
            <Box key={`group-${group[0].id}`}>
              {/* ODER separator between groups */}
              {groupIdx > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#ef5350', opacity: 0.5 }} />
                  <Chip label="ODER" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 700, fontSize: 11, height: 22 }} />
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#ef5350', opacity: 0.5 }} />
                </Box>
              )}

              {/* UND drop zone before first item of group */}
              {renderFilterAndZone(group[0].globalIdx)}

              {group.map((cond) => (
                <Box key={cond.id}>
                  {renderFilterCondRow(cond)}
                  {renderFilterAndZone(cond.globalIdx + 1)}
                </Box>
              ))}

              {/* "+" button to add AND condition to this group */}
              {dragFilterIdx === null && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                  <Box component="span"
                    onClick={() => addCondToGroup(groupIdx)}
                    sx={{
                      color: '#42a5f5', fontSize: 18, lineHeight: 1, cursor: 'pointer', fontWeight: 700,
                      px: 1.25, py: 0.25, borderRadius: '50%',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { bgcolor: 'rgba(66,165,245,0.12)' },
                    }}
                    title="UND-Bedingung zu dieser Gruppe hinzufügen"
                  >+</Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Divider + action / ODER-zone area */}
          <Box sx={{ mt: 1.5, borderTop: '1px solid rgba(21,101,192,0.25)' }}>
            {/* ODER drop zone — always 36px, visible only while dragging (opacity toggle, no height change) */}
            {(() => {
              const pos = arrayFilters.length;
              const isDragging = dragFilterIdx !== null;
              const isActive = isDragging && dragOverTarget?.pos === pos && dragOverTarget.type === 'OR';
              return (
                <Box
                  onDragOver={(e) => { if (isDragging) { e.preventDefault(); setDragOverTarget({ pos, type: 'OR' }); } }}
                  onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node) && dragOverTarget?.pos === pos) setDragOverTarget(null); }}
                  onDrop={(e) => { if (isDragging) { e.preventDefault(); handleFilterDrop(pos, 'OR'); } }}
                  sx={{
                    height: 36,
                    mt: 0.5,
                    borderRadius: 1,
                    opacity: isDragging ? 1 : 0,
                    pointerEvents: isDragging ? 'auto' : 'none',
                    border: isActive ? '2px dashed #ef5350' : '1px dashed rgba(239,83,80,0.35)',
                    bgcolor: isActive ? 'rgba(239,83,80,0.1)' : 'rgba(239,83,80,0.02)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 0.1s, background-color 0.1s, opacity 0.1s',
                  }}
                >
                  <Typography variant="caption" sx={{ color: isActive ? '#ef5350' : 'rgba(239,83,80,0.45)', fontWeight: 700, fontSize: 10, userSelect: 'none' }}>
                    + ODER (neue Gruppe)
                  </Typography>
                </Box>
              );
            })()}

            {/* Action buttons — always in DOM, hidden while dragging */}
            <Box sx={{
              pt: 1.5, display: 'flex', gap: 1,
              opacity: dragFilterIdx !== null ? 0 : 1,
              pointerEvents: dragFilterIdx !== null ? 'none' : 'auto',
              transition: 'opacity 0.1s',
            }}>
              <Button size="small" startIcon={<AddIcon />}
                onClick={() => {
                  // Neue ODER-Gruppe: gleiches Array wie die letzte Bedingung
                  const newCond: ArrayFilterCond = { id: crypto.randomUUID(), field: availableFilterFields[0] ?? '', operator: 'equals', value: '', at: arrayFilters[arrayFilters.length - 1]?.at };
                  setArrayFilters((prev) => [...prev, newCond]);
                  if (arrayFilters.length > 0) setArrayFilterLogic((prev) => [...prev, 'OR']);
                }}
                sx={{ color: '#90caf9' }}>
                Bedingung
              </Button>
              <Button size="small" variant="contained"
                disabled={!arrayFilters.some((f) => f.field && f.value) || simLoading}
                onClick={() => void runSimulation()}
                sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                Anwenden
              </Button>
              {arrayFilters.length > 0 && (
                <Button size="small"
                  onClick={() => { setPickNotice(null); setArrayFilters([]); setArrayFilterLogic([]); void runSimulation(undefined, undefined, [], []); }}
                  sx={{ color: '#90caf9' }}>
                  Zurücksetzen
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Paper>
  ) : null;

  // Ein Wert mit bekanntem Fundort ist anklickbar: Pfad + Filter werden daraus abgeleitet
  const renderJsonValue = (content: React.ReactNode, style: React.CSSProperties, ptr: PointerSegment[] | null, busy: boolean): React.ReactNode => {
    if (!ptr || busy) return <span style={style}>{content}</span>;
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => void handleValueClick(ptr)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void handleValueClick(ptr); } }}
        title="Wert übernehmen — Pfad und Array-Filter werden gesetzt"
        style={{ ...style, cursor: 'pointer', borderBottom: '1px dashed currentColor', borderRadius: 3, padding: '0 2px', transition: 'background 0.15s' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(144,202,249,0.16)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {content}
      </span>
    );
  };

  const renderInteractiveJson = (val: unknown, indent: number, onKeyClick: (key: string) => void, busy: boolean, pathPrefix = '', ptr: PointerSegment[] | null = null): React.ReactNode => {
    if (val === null || val === undefined) return renderJsonValue('null', { color: '#888', fontStyle: 'italic' }, val === null ? ptr : null, busy);
    if (typeof val === 'boolean') return renderJsonValue(String(val), { color: '#f0b429', fontWeight: 600 }, ptr, busy);
    if (typeof val === 'number') return renderJsonValue(val, { color: '#79c0ff' }, ptr, busy);
    if (typeof val === 'string') return renderJsonValue(`"${val}"`, { color: '#a5d6a7' }, ptr, busy);
    if (Array.isArray(val)) {
      if (val.length === 0) return <span style={{ color: '#888' }}>[ ]</span>;
      return (
        <>
          <span style={{ color: '#888' }}>[</span>
          {(val as unknown[]).map((item, idx) => (
            <div key={idx} style={{ paddingLeft: (indent + 1) * 16, borderLeft: '1px solid #333', marginLeft: 4 }}>
              {renderInteractiveJson(item, indent + 1, onKeyClick, busy, pathPrefix, ptr ? [...ptr, idx] : null)}
              {idx < val.length - 1 && <span style={{ color: '#555' }}>,</span>}
            </div>
          ))}
          <div style={{ paddingLeft: indent * 16 }}><span style={{ color: '#888' }}>]</span></div>
        </>
      );
    }
    // Object
    const entries = Object.entries(val as Record<string, unknown>);
    if (entries.length === 0) return <span style={{ color: '#888' }}>{'{ }'}</span>;
    return (
      <>
        <span style={{ color: '#888' }}>{'{'}</span>
        {entries.map(([k, v], idx) => (
          <div key={k} style={{ paddingLeft: (indent + 1) * 16, borderLeft: '1px solid #2a2a2a', marginLeft: 4, paddingTop: 2, paddingBottom: 2 }}>
            <span
              onClick={!busy ? () => onKeyClick(pathPrefix ? `${pathPrefix}.${k}` : k) : undefined}
              style={{
                display: 'inline-block',
                background: busy ? '#1e3a5f88' : '#1e3a5f',
                color: '#90caf9',
                borderRadius: 20,
                padding: '1px 9px',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: busy ? 'default' : 'pointer',
                marginRight: 6,
                border: '1px solid #2d5a9e',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={!busy ? (e) => { (e.currentTarget as HTMLElement).style.background = '#2d5a9e'; } : undefined}
              onMouseLeave={!busy ? (e) => { (e.currentTarget as HTMLElement).style.background = '#1e3a5f'; } : undefined}
              title={busy ? undefined : `"${k}" auswählen`}
            >
              {k}
            </span>
            <span style={{ color: '#555', marginRight: 6 }}>:</span>
            {renderInteractiveJson(v, indent + 1, onKeyClick, busy, pathPrefix ? `${pathPrefix}.${k}` : k, ptr ? [...ptr, k] : null)}
            {idx < entries.length - 1 && <span style={{ color: '#444' }}>,</span>}
          </div>
        ))}
        <div style={{ paddingLeft: indent * 16 }}><span style={{ color: '#888' }}>{'}'}</span></div>
      </>
    );
  };

  // ── Resolved value renderer — shows all ambiguous items individually ──
  const renderResolvedContent = (onKeyClick: (key: string) => void, busy: boolean): React.ReactNode => {
    if (resolvedValue === null) return null;
    const location = resolvedLocation;
    if (isAmbiguous && Array.isArray(resolvedValue) && resolvedValue.length > 1) {
      return (
        <>
          <Typography variant="caption" sx={{ color: '#90caf9', display: 'block', mb: 1, fontSize: '0.75rem' }}>
            {resolvedValue.length} passende Array-Einträge — wähle oben einen Filter oder klicke einen Wert an:
          </Typography>
          {(resolvedValue as unknown[]).map((item, idx) => (
            <Box key={idx}>
              {idx > 0 && <Box sx={{ height: '1px', bgcolor: '#21262d', my: 1 }} />}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 22, pt: '3px' }}>
                  <Typography variant="caption" sx={{ color: '#555', fontFamily: 'monospace', fontSize: '0.72rem' }}>[{idx}]</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {renderInteractiveJson(item, 0, onKeyClick, busy, '', location?.kind === 'list' ? (location.pointers[idx] ?? null) : null)}
                </Box>
              </Box>
            </Box>
          ))}
        </>
      );
    }
    return renderInteractiveJson(resolvedValue, 0, onKeyClick, busy, '', location?.kind === 'node' ? location.pointer : null);
  };

  const pickNoticeBox: React.ReactNode = pickNotice ? (
    <Alert severity={pickNotice.severity} sx={{ mb: 2 }} onClose={() => setPickNotice(null)} role="status">
      {pickNotice.text}
    </Alert>
  ) : null;

  const valueClickHint = resolvedLocation ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      Werte sind anklickbar — Pfad und Array-Filter werden dann automatisch gesetzt.
    </Typography>
  ) : null;

  return (
    <Box
      component="section"
      aria-label="Check Library Konfiguration+"
      sx={{
        '& .MuiInputLabel-root.Mui-focused': { color: '#1f2937 !important' },
        '& .MuiFormLabel-root.Mui-focused': { color: '#1f2937 !important' },
        '& .MuiInputLabel-root.Mui-focused.MuiInputLabel-shrink': { color: '#1f2937 !important' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#1f2937 !important',
          borderWidth: '1px',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4b5563' },
        '& .MuiInputBase-root.Mui-focused': { color: '#111827' },
        '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
        '& .MuiSelect-icon': { color: '#6b7280' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Check Library</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Checks aus der checks_plus Tabelle. Klicke auf eine Karte für Bearbeitung und Details.
      </Typography>

      {/* Row 1: Search + Inactive-Switch + Check anlegen */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'nowrap' }}>
        <TextField
          size="small"
          placeholder="Suche nach Code, Titel, Beschreibung…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Checks durchsuchen' }}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Inaktive einblenden</Typography>}
          sx={{ ml: 0, flexShrink: 0 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ bgcolor: colors.lime, color: colors.black, fontWeight: 700, flexShrink: 0, '&:hover': { bgcolor: '#b8d600' }, whiteSpace: 'nowrap' }}
        >
          Check anlegen
        </Button>
      </Box>

      {/* Row 2: Label filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
        <Chip
          label="Alle"
          size="medium"
          onClick={() => {
            const allSelected = activeLabelIds.size === allLabels.length && allLabels.length > 0;
            setActiveLabelIds(
              allSelected
                ? new Set()                                           // alle → deselect all
                : new Set(allLabels.map((l) => String(l.id))),        // any other → select all
            );
          }}
          sx={{
            height: 32,
            fontWeight: 700,
            fontSize: 12,
            bgcolor: activeLabelIds.size === allLabels.length && allLabels.length > 0 ? colors.black : '#f3f4f6',
            color: activeLabelIds.size === allLabels.length && allLabels.length > 0 ? colors.lime : '#374151',
            '&:hover': activeLabelIds.size === allLabels.length && allLabels.length > 0
              ? { bgcolor: '#f3f4f6', color: '#1E1E1E' }
              : { bgcolor: colors.black, color: colors.lime },
          }}
        />
        {allLabels.map((label) => (
          <Chip
            key={label.id}
            label={label.name}
            size="medium"
            onClick={() =>
              setActiveLabelIds((prev) => {
                const next = new Set(prev);
                if (next.has(String(label.id))) next.delete(String(label.id));
                else next.add(String(label.id));
                return next;
              })
            }
            sx={{
              height: 32,
              fontWeight: 700,
              fontSize: 12,
              maxWidth: 160,
              bgcolor: activeLabelIds.has(String(label.id)) ? colors.black : '#f3f4f6',
              color: activeLabelIds.has(String(label.id)) ? colors.lime : '#374151',
              '&:hover': activeLabelIds.has(String(label.id))
                ? { bgcolor: '#f3f4f6', color: '#1E1E1E' }
                : { bgcolor: colors.black, color: colors.lime },
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} role="alert">{error}</Alert>}
      {loading && (
        <Box sx={{ py: 8, textAlign: 'center' }} role="status" aria-live="polite" aria-label="Checks werden geladen">
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && (
        <Box
          role="region"
          aria-label="Check Library Kanban Board"
          sx={{
            width: 'min(1256px, 100vw)',
            ml: 'calc((min(1256px, 100vw) - 100%) / -2)',
            px: 3,
          }}
        >
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={(event) => void handleDragEnd(event)}
          >
            <Box sx={{ position: 'relative' }}>
              {/* Left scroll fade */}
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 8, width: 56, zIndex: 2, pointerEvents: 'none',
                background: 'linear-gradient(to right, rgba(255,255,255,1) 20%, transparent)',
                opacity: kanbanScroll.canLeft ? 1 : 0,
                transition: 'opacity 200ms',
              }} />
              {/* Right scroll fade */}
              <Box sx={{
                position: 'absolute', right: 0, top: 0, bottom: 8, width: 80, zIndex: 2, pointerEvents: 'none',
                background: 'linear-gradient(to left, rgba(255,255,255,1) 20%, transparent)',
                opacity: kanbanScroll.canRight ? 1 : 0,
                transition: 'opacity 200ms',
              }} />
              <Box
                ref={kanbanScrollRef}
                onScroll={updateKanbanScroll}
                sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}
              >
              {activeLabelIds.size === 0 ? (
                <Box sx={{ py: 6, px: 3, color: '#9ca3af', fontSize: 14, fontStyle: 'italic' }}>
                  Bitte wähle ein Label aus, um Checks anzuzeigen.
                </Box>
              ) : (
                allLabels
                  .filter((label) => activeLabelIds.has(String(label.id)))
                  .map((label) => (
                    <CategoryColumn
                      key={`label-${label.id}`}
                      columnId={String(label.id)}
                      title={label.name}
                      checks={checksByLabel[String(label.id)] ?? []}
                      selectedCheckId={selectedCheckId}
                      onSelectCheck={(id) => setSelectedCheckId(id)}
                    />
                  ))
              )}
              <CategoryColumn
                key="label-no-label"
                columnId="no_label"
                title="Ohne Label"
                checks={checksByLabel.no_label ?? []}
                selectedCheckId={selectedCheckId}
                onSelectCheck={(id) => setSelectedCheckId(id)}
              />
            </Box>
            </Box>
            <DragOverlay>
              {activeDragCheck ? <DragPreviewCard check={activeDragCheck} /> : null}
            </DragOverlay>
          </DndContext>
        </Box>
      )}

      {/* ── Detail Dialog ──────────────────────────────────── */}
      <Dialog
        open={Boolean(selectedCheck)}
        onClose={() => setSelectedCheckId(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${colors.silver}`,
            bgcolor: '#fbfbfb',
            width: '92vw',
            maxWidth: '1200px',
            minHeight: 'unset',
            height: 'auto',
          },
        }}
      >
        {selectedCheck && (
          <DialogContent sx={{ p: 1.75 }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Typography variant="h6">
                ⚙️ Check Library › <Box component="span" sx={{ cursor: 'pointer' }} onClick={() => setSelectedCheckId(null)}>{selectedCategory?.display_name ?? '-'}</Box> › {selectedCheck.check_code}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2.25, alignItems: 'start' }}>

              {/* ── Left column: Engine & Regeln (full height) ── */}
              <Paper sx={{ p: 2.25, borderRadius: 3, border: `1px solid ${colors.silver}`, minHeight: 250, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Engine und Regeln</Typography>

                <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
                  <InputLabel>Engine Mode</InputLabel>
                  <Select
                    value={selectedCheck.engine_mode ?? EngineMode.RULE_ENGINE}
                    label="Engine Mode"
                    onChange={(e) => handleFieldUpdate(selectedCheck.id, 'engine_mode', e.target.value)}
                  >
                    <MenuItem value={EngineMode.LEGACY}>Legacy (Mapping Rules)</MenuItem>
                    <MenuItem value={EngineMode.RULE_ENGINE}>Rule Engine (Konfiguration+)</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedCheck.active}
                        onChange={(e) => handleFieldUpdate(selectedCheck.id, 'active', e.target.checked)}
                      />
                    }
                    label="Check aktiv"
                  />
                  {devMode && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedCheck.dev_only ?? false}
                          onChange={(e) => handleFieldUpdate(selectedCheck.id, 'dev_only', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Entwickler Only</Typography>}
                    />
                  )}
                </Box>

                {canEditRules && (
                  <Box sx={{ mb: 2 }}>
                    {savedRuleGroups.length > 0 && savedRuleGroups[0]?.field_path ? (
                      <Stack spacing={1.25}>
                        <Button
                          variant="outlined"
                          startIcon={<ScienceIcon />}
                          onClick={() => openPathDialog(selectedCheck.id)}
                          size="small"
                          fullWidth
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          Pfad anpassen
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => openRulesDialog(selectedCheck.id)}
                          size="small"
                          fullWidth
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          Regeln anpassen
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<ScienceIcon />}
                        onClick={() => openSimDialog(selectedCheck.id)}
                        size="small"
                        fullWidth
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        Playwright Raw Data Explorer
                      </Button>
                    )}
                  </Box>
                )}

                <Box sx={{ mt: 'auto' }}>
                  <Paper
                    variant="outlined"
                    sx={{ borderRadius: 2, bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column', height: 380 }}
                  >
                    <Box sx={{ p: 1.5, pb: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
                        Konfigurierte Regeln
                      </Typography>
                      {savedRuleGroups.length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.4 }}>
                            Datenpfad
                          </Typography>
                          <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1, px: 1, py: 0.6 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: '#374151', wordBreak: 'break-all' }}>
                              {savedRuleGroups[0]?.field_path}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {savedRuleGroups.length > 0 ? (
                      <Box sx={{ flex: '1 1 0', overflowY: 'auto', p: 1.25, '& > *': { mb: 1 }, '& > *:last-child': { mb: 0 } }}>
                        {savedRuleGroups.map((group) => {
                          const groupsOfRating = savedRuleGroups.filter((g) => g.result_rating === group.result_rating);
                          const idxWithinRating = groupsOfRating.findIndex((g) => g.id === group.id);
                          const ratingColor = group.result_rating === 'G' ? colors.scoreGreen : group.result_rating === 'Y' ? colors.scoreYellow : colors.scoreRed;
                          const ratingTextColor = group.result_rating === 'G' ? '#fff' : group.result_rating === 'Y' ? '#000' : '#fff';
                          const ratingLabel = group.result_rating === 'G' ? 'Grün' : group.result_rating === 'Y' ? 'Gelb' : 'Rot';
                          return (
                            <Box key={group.id} sx={{ borderRadius: 1.5, border: `1px solid ${ratingColor}`, bgcolor: '#fff', overflow: 'hidden' }}>
                              <Box sx={{ px: 1.25, py: 0.6, bgcolor: ratingColor, borderBottom: `1px solid ${ratingColor}`, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#000', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  {groupsOfRating.length > 1 ? `Gruppe ${idxWithinRating + 1}` : 'Gruppe 1'}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box>
                                  <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.4 }}>Bedingungen</Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {group.rules.map((rule, ruleIdx) => (
                                      <Box key={rule.id}>
                                        {ruleIdx > 0 && (
                                          <Typography variant="caption" sx={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, display: 'block', textAlign: 'center', lineHeight: 1.6 }}>— UND —</Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                                          <Box sx={{ flex: '0 0 25%', bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 1, px: 0.75, py: 0.6 }}>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: '#374151', fontWeight: 600, wordBreak: 'break-all' }}>{rule.operator_key}</Typography>
                                          </Box>
                                          <Box sx={{ flex: '1 1 0', bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 1, px: 0.75, py: 0.6 }}>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: rule.expected_value ? '#374151' : '#9ca3af', fontStyle: rule.expected_value ? 'normal' : 'italic', wordBreak: 'break-all' }}>
                                              {rule.expected_value || '—'}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Noch keine Regeln konfiguriert. Nutze den Playwright Raw Data Explorer, um Regeln zu definieren.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Paper>{/* end left column */}

              {/* ── Right column: Grunddaten + Labels + Buttons ── */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, alignSelf: 'stretch' }}>

                {/* Grunddaten */}
                <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${colors.silver}` }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Grunddaten</Typography>
                  {(() => {
                    const draft = grunddatenDraft ?? { title: selectedCheck.title, description: selectedCheck.description ?? '', info_box: selectedCheck.info_box ?? '' };
                    const update = (field: string, value: string) => setGrunddatenDraft({ ...draft, [field]: value });
                    return (
                      <>
                        <Box sx={{ display: 'flex', gap: 2, mb: 1.25, flexWrap: 'wrap' }}>
                          <TextField
                            label="Titel"
                            value={draft.title}
                            size="small"
                            sx={{ flex: '1 1 220px', minWidth: 220 }}
                            onChange={(e) => update('title', e.target.value)}
                          />
                        </Box>
                        <TextField
                          label="Beschreibung"
                          value={draft.description}
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          onChange={(e) => update('description', e.target.value)}
                        />
                        <TextField
                          label="Info-Box"
                          value={draft.info_box}
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mt: 1.25 }}
                          onChange={(e) => update('info_box', e.target.value)}
                        />
                      </>
                    );
                  })()}
                </Paper>

                {/* Labels */}
                <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${colors.silver}`, position: 'relative', zIndex: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Labels</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
                    {allLabels.map((label) => {
                      const isAssigned = (selectedCheck.labels ?? []).some((l) => l.id === label.id);
                      return (
                        <Box key={label.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                          <Chip
                            label={label.name}
                            size="small"
                            onClick={() => handleToggleLabel(selectedCheck, label.id)}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: isAssigned ? (label.color ?? colors.black) : 'transparent',
                              color: isAssigned ? '#fff' : colors.black60,
                              border: `1px solid ${isAssigned ? (label.color ?? colors.black) : colors.silver}`,
                              borderRadius: '16px 0 0 16px',
                              '&:hover': { opacity: 0.8 },
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${colors.silver}`, borderLeft: 'none', borderRadius: '0 16px 16px 0', px: 0.25, height: 24, bgcolor: 'transparent' }}>
                            <IconButton size="small" aria-label={`Label ${label.name} bearbeiten`} onClick={() => { setEditLabel(label); setEditLabelName(label.name); }} sx={{ p: 0.25 }}>
                              <EditIcon sx={{ fontSize: 13, color: colors.black60 }} />
                            </IconButton>
                            <IconButton size="small" aria-label={`Label ${label.name} löschen`} onClick={() => setDeleteLabelTarget(label)} sx={{ p: 0.25 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 13, color: colors.black60 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Neues Label…"
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLabel(); }}
                      sx={{ width: 220 }}
                    />
                    <IconButton size="small" aria-label="Neues Label erstellen" onClick={handleCreateLabel} disabled={!newLabelName.trim()}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>

                {/* Action buttons — pushed to bottom of right column */}
                <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => setDeleteCheckStep(1)}
                    sx={{ textTransform: 'none', color: '#fff' }}
                  >
                    Löschen
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { handleCancelGrunddaten(); setSelectedCheckId(null); }}
                    sx={{ textTransform: 'none', borderColor: '#9ca3af', color: '#6b7280', '&:hover': { borderColor: '#6b7280', bgcolor: '#f9fafb' } }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={async () => { await handleSaveGrunddaten(); setSelectedCheckId(null); }}
                    disabled={!grunddatenDraft || grunddatenSaving}
                    sx={{ textTransform: 'none', bgcolor: '#16a34a', color: '#000', '&:hover': { bgcolor: '#15803d' }, '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
                  >
                    {grunddatenSaving ? 'Speichert…' : 'Speichern'}
                  </Button>
                </Box>

              </Box>{/* end right column */}

            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* ── Label Edit Dialog ──────────────────────────────── */}
      <Dialog open={!!editLabel} onClose={() => setEditLabel(null)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Label bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Name"
            value={editLabelName}
            onChange={(e) => setEditLabelName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); }}
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLabel(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveLabel} disabled={!editLabelName.trim()}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* ── Label Delete Dialog ────────────────────────────── */}
      <Dialog open={!!deleteLabelTarget} onClose={() => setDeleteLabelTarget(null)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Label löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du das Label <strong>{deleteLabelTarget?.name}</strong> wirklich löschen? Es wird von allen Checks entfernt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLabelTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDeleteLabel}>Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Check Step 1 ────────────────────────────── */}
      <Dialog open={deleteCheckStep === 1} onClose={() => setDeleteCheckStep(0)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Check löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du den Check <strong>{checks.find((c) => c.id === selectedCheckId)?.check_code}</strong> wirklich löschen?
            Alle konfigurierten Regeln werden unwiderruflich entfernt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCheckStep(0)} sx={{ color: '#6b7280' }}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={() => setDeleteCheckStep(2)}>
            Ja, löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Check Step 2 (final confirmation) ──────── */}
      <Dialog open={deleteCheckStep === 2} onClose={() => setDeleteCheckStep(0)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle sx={{ color: '#dc2626' }}>⚠️ Endgültig löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
          <Typography variant="body2">
            Bitte bestätige, dass du den Check <strong>{checks.find((c) => c.id === selectedCheckId)?.check_code}</strong> endgültig und unwiderruflich aus der Datenbank löschen möchtest.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCheckStep(0)} sx={{ color: '#6b7280' }}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCheck} disabled={deleteCheckSaving}>
            {deleteCheckSaving ? 'Löscht…' : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Playwright Raw Data Explorer Dialog ──────────────── */}
      <Dialog open={simDialogOpen} onClose={() => setSimDialogOpen(false)} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>Playwright Raw Data Explorer</DialogTitle>
        <DialogContent>
          <Autocomplete
            disablePortal
            sx={{ mt: 1, mb: 2 }}
            options={scans}
            value={selectedScan}
            inputValue={scanSearchInput}
            onChange={(_, newValue) => {
              setSelectedScan(newValue);
              setSelectedScanId(newValue?.id ?? '');
              setScanSearchInput(newValue?.url ?? '');
            }}
            onInputChange={(_, newInput, reason) => {
              if (reason === 'input') setScanSearchInput(newInput);
              if (reason === 'clear') { setScanSearchInput(''); setSelectedScan(null); setSelectedScanId(''); }
            }}
            filterOptions={(options, state) => {
              const q = state.inputValue.trim().toLowerCase();
              if (!q) return options.slice(0, 20);
              return options.filter((o) => o.url.toLowerCase().includes(q));
            }}
            getOptionLabel={(option) => option.url}
            noOptionsText="Keine Scans gefunden"
            ListboxProps={{ style: { maxHeight: 320 } }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scan auswählen"
                size="small"
                placeholder="Domain oder URL eingeben…"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ px: '12px !important', py: '8px !important', alignItems: 'flex-start !important' }}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                    {option.url}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      📅 {new Date(option.created_at).toLocaleDateString('de-DE')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      🕐 {new Date(option.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {option.engine_name && (
                      <Chip label={`⚡ ${option.engine_name}`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                    )}
                    {option.user_name && (
                      <Typography sx={{ fontSize: 11, color: '#555' }}>
                        👤 {option.user_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          />

          {/* Field Path Explorer — Cascading Dropdowns */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Field Path Explorer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Wähle den Pfad in der Playwright-JSON-Struktur (basierend auf den letzten 20 Scans):
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {fieldPathLevels.map((level, idx) => (
                <FormControl key={idx} size="small" fullWidth>
                  <InputLabel>
                    {idx === 0 ? 'Ebene 1 (Root)' : `Ebene ${idx + 1}`}
                  </InputLabel>
                  <Select
                    value={level.selected}
                    label={idx === 0 ? 'Ebene 1 (Root)' : `Ebene ${idx + 1}`}
                    onChange={(e) => handleFieldPathSelect(idx, e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value="">— Auswählen —</MenuItem>
                    {level.keys.map((k) => (
                      <MenuItem key={k.key} value={k.key}>
                        {k.key}
                        {k.hasChildren && ' 📂'}
                        {!k.hasChildren && ` (${k.type})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              {fieldPathLoading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
            </Box>

            {computedFieldPath && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: colors.black, borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: colors.lime }}>
                  {computedFieldPath}
                </Typography>
              </Box>
            )}
          </Paper>

          {simLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Wert wird aufgelöst…</Typography>
            </Box>
          )}

          {/* Resolved Value Output */}
          {arrayFilterPanel}
          {pickNoticeBox}
          {resolveError && <Alert severity="warning" sx={{ mb: 2 }}>{resolveError}</Alert>}
          {resolvedValue !== null && !resolveError && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  Aufgelöster Wert bei <code>{computedFieldPath}</code>:
                </Typography>
                <IconButton size="small" onClick={() => setResolvedValuePopupOpen(true)} title="Aufklappen">
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Box>
              {valueClickHint}
              <Box sx={{ p: 2, bgcolor: '#0d1117', borderRadius: 2, maxHeight: 250, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.8, border: '1px solid #21262d' }}>
                {renderResolvedContent(handlePopupKeyClick, fieldPathLoading || simLoading)}
              </Box>
            </Paper>
          )}
          {resolvedValue !== null && !resolveError && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Bewertungsregeln definieren
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Definiere Bedingungsgruppen: Bedingungen innerhalb einer Gruppe sind UND-verknüpft.
                Gruppen untereinander sind ODER-verknüpft (mind. eine Gruppe muss erfüllt sein).
                {Array.isArray(resolvedValue) && (
                  <><br /><strong>Array erkannt</strong> — nutze Array-Operatoren wie &quot;Array enthält nur&quot; oder &quot;Array hat Extra-Einträge&quot;.</>
                )}
              </Typography>

              {/* Auto-Red Fallback Toggle */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fff8f8', borderColor: colors.scoreRed }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRedFallback}
                      onChange={(e) => setAutoRedFallback(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <strong>Auto-Rot-Fallback:</strong> Alles was nicht Grün oder Gelb matcht → automatisch Rot
                    </Typography>
                  }
                />
              </Paper>

              {/* ── Rating Sections: Green / Yellow / Red ── */}
              {(['G', 'Y', 'R'] as const).map((rating) => {
                const ratingLabel = rating === 'G' ? 'Grün (OK)' : rating === 'Y' ? 'Gelb (Optimierungsbedarf)' : `Rot (Kritisch)${autoRedFallback ? ' — explizit (optional)' : ''}`;
                const ratingColor = rating === 'G' ? colors.scoreGreen : rating === 'Y' ? '#b8860b' : colors.scoreRed;
                const groupsForRating = ruleGroups.filter((g) => g.rating === rating);

                return (
                  <Box key={rating} sx={{ mb: 3 }}>
                    {/* Rating Header + Add first group */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ratingColor }} />
                      <Typography variant="subtitle2" sx={{ color: ratingColor }}>{ratingLabel}</Typography>
                      {groupsForRating.length === 0 && (
                        <IconButton size="small" onClick={() => addFirstCondition(rating)} aria-label={`${ratingLabel} Bedingung hinzufügen`}>
                          <AddIcon fontSize="small" sx={{ color: ratingColor }} />
                        </IconButton>
                      )}
                    </Box>

                    {rating === 'R' && autoRedFallback && groupsForRating.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5, display: 'block' }}>
                        Auto-Rot aktiv. Zusätzliche explizite Rot-Regeln optional.
                      </Typography>
                    )}

                    {/* Groups */}
                    {groupsForRating.map((group, groupIdx) => (
                      <Box key={group.id}>
                        {/* OR Separator between groups */}
                        {groupIdx > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, ml: 2 }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: ratingColor, opacity: 0.4 }} />
                            <Chip
                              label="ODER"
                              size="small"
                              sx={{ bgcolor: ratingColor, color: '#fff', fontWeight: 700, fontSize: 11, height: 22 }}
                            />
                            <Box sx={{ flex: 1, height: '1px', bgcolor: ratingColor, opacity: 0.4 }} />
                          </Box>
                        )}

                        {/* Group Box (droppable area) */}
                        <Paper
                          variant="outlined"
                          sx={{
                            ml: 2.5, p: 1.5, borderRadius: 2,
                            borderColor: ratingColor, borderStyle: 'dashed',
                            bgcolor: draggedCondition && draggedCondition.fromGroupId !== group.id ? '#f5f5f5' : 'transparent',
                          }}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedCondition) {
                              moveConditionToGroup(draggedCondition.conditionId, draggedCondition.fromGroupId, group.id);
                              setDraggedCondition(null);
                              setOrDropAfterGroupId(null);
                              setRawDropRating(null);
                            }
                          }}
                        >
                          <Typography variant="caption" sx={{ color: ratingColor, fontWeight: 600, mb: 0.5, display: 'block' }}>
                            Gruppe {groupIdx + 1} (UND-verknüpft)
                          </Typography>

                          {/* Conditions in this group */}
                          {group.conditions.map((cond, condIdx) => (
                            <Box key={cond.id}>
                              {/* AND label between conditions */}
                              {condIdx > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, my: 0.5 }}>
                                  <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#e0e0e0' }} />
                                </Box>
                              )}
                              <Box
                                sx={{
                                  display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap',
                                  p: 0.5, borderRadius: 1,
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                                }}
                              >
                                <Box
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/plain', cond.id);
                                    setTimeout(() => setDraggedCondition({ conditionId: cond.id, fromGroupId: group.id }), 0);
                                  }}
                                  onDragEnd={() => {
                                    setDraggedCondition(null);
                                    setOrDropAfterGroupId(null);
                                    setRawDropRating(null);
                                  }}
                                  sx={{
                                    width: 24,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'grab',
                                    color: '#6b7280',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: '#f3f4f6', color: '#111827' },
                                    '&:active': { cursor: 'grabbing' },
                                  }}
                                  title="Regel ziehen"
                                  aria-label="Regel ziehen"
                                >
                                  <DragIndicatorIcon sx={{ fontSize: 18 }} />
                                </Box>

                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                  <InputLabel>Operator</InputLabel>
                                  <Select value={cond.operator} label="Operator" renderValue={operatorLabel} onChange={(e) => updateConditionInGroup(group.id, cond.id, 'operator', e.target.value)} MenuProps={OPERATOR_MENU_PROPS}>
                                    {renderOperatorItems()}
                                  </Select>
                                </FormControl>
                                {cond.operator === 'array_has_key_value' ? (
                                  <>
                                    <TextField
                                      size="small"
                                      label="Key"
                                      value={cond.expected_value.includes('=') ? cond.expected_value.slice(0, cond.expected_value.indexOf('=')) : cond.expected_value}
                                      onChange={(e) => {
                                        const v = cond.expected_value.includes('=') ? cond.expected_value.slice(cond.expected_value.indexOf('=') + 1) : '';
                                        updateConditionInGroup(group.id, cond.id, 'expected_value', `${e.target.value}=${v}`);
                                      }}
                                      sx={{ flex: '1 1 110px' }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Value"
                                      value={cond.expected_value.includes('=') ? cond.expected_value.slice(cond.expected_value.indexOf('=') + 1) : ''}
                                      onChange={(e) => {
                                        const k = cond.expected_value.includes('=') ? cond.expected_value.slice(0, cond.expected_value.indexOf('=')) : cond.expected_value;
                                        updateConditionInGroup(group.id, cond.id, 'expected_value', `${k}=${e.target.value}`);
                                      }}
                                      sx={{ flex: '1 1 110px' }}
                                    />
                                  </>
                                ) : (
                                  <TextField
                                    size="small"
                                    label="Erwarteter Wert"
                                    value={cond.expected_value}
                                    onChange={(e) => updateConditionInGroup(group.id, cond.id, 'expected_value', e.target.value)}
                                    sx={{ flex: '1 1 150px' }}
                                    disabled={['exists', 'not_exists', 'is_true', 'is_false', 'array_is_empty', 'array_not_empty'].includes(cond.operator)}
                                    helperText={operatorHelperText(cond.operator)}
                                  />
                                )}
                                <IconButton size="small" onClick={() => removeConditionFromGroup(group.id, cond.id)} aria-label="Bedingung entfernen">
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}

                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => addConditionToGroup(group.id)}
                              sx={{ fontSize: 11, textTransform: 'none', borderColor: '#bbb', color: '#555' }}
                            >
                              + UND (Bedingung hinzufügen)
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => addNewGroupAfter(group.id)}
                              sx={{ fontSize: 11, textTransform: 'none', borderColor: ratingColor, color: ratingColor }}
                            >
                              + ODER (neue Gruppe)
                            </Button>
                          </Box>
                        </Paper>

                        {/* Always rendered to avoid reflow; hidden via CSS when not dragging */}
                        <Box
                          onDragOver={(e) => {
                            if (!draggedCondition) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setOrDropAfterGroupId(group.id);
                          }}
                          onDragLeave={() => {
                            if (orDropAfterGroupId === group.id) {
                              setOrDropAfterGroupId(null);
                            }
                          }}
                          onDrop={(e) => {
                            if (!draggedCondition) return;
                            e.preventDefault();
                            moveConditionToNewGroupAfter(draggedCondition.conditionId, draggedCondition.fromGroupId, group.id);
                            setDraggedCondition(null);
                            setOrDropAfterGroupId(null);
                            setRawDropRating(null);
                          }}
                          sx={{
                            ml: 2.5,
                            mt: 1,
                            p: 1,
                            borderRadius: 1.5,
                            border: '1px dashed #9ca3af',
                            bgcolor: orDropAfterGroupId === group.id ? '#e5e7eb' : '#f3f4f6',
                            transition: 'opacity 0.15s ease, background-color 0.15s ease',
                            visibility: draggedCondition ? 'visible' : 'hidden',
                            opacity: draggedCondition ? 1 : 0,
                            pointerEvents: draggedCondition ? 'auto' : 'none',
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600 }}>
                            Hier ablegen, um eine neue ODER-Gruppe zu erstellen
                          </Typography>
                        </Box>
                      </Box>
                    ))}

                    {/* Rating-level drop zone — shown AFTER groups to avoid layout reflow killing the drag */}
                    <Paper
                      variant="outlined"
                      onDragOver={(e) => {
                        if (!draggedCondition || groupsForRating.length > 0) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setRawDropRating(rating);
                      }}
                      onDragLeave={() => {
                        if (rawDropRating === rating) setRawDropRating(null);
                      }}
                      onDrop={(e) => {
                        if (!draggedCondition || groupsForRating.length > 0) return;
                        e.preventDefault();
                        moveConditionToNewGroupInRating(draggedCondition.conditionId, draggedCondition.fromGroupId, rating);
                        setDraggedCondition(null);
                        setOrDropAfterGroupId(null);
                        setRawDropRating(null);
                      }}
                      sx={{
                        ml: 2.5,
                        mt: (draggedCondition && groupsForRating.length === 0) ? 1 : 0,
                        p: (draggedCondition && groupsForRating.length === 0) ? 1.5 : 0,
                        borderRadius: 1.5,
                        borderStyle: 'dashed',
                        borderColor: rawDropRating === rating ? ratingColor : '#9ca3af',
                        bgcolor: rawDropRating === rating ? '#e5e7eb' : '#f3f4f6',
                        transition: 'all 0.15s ease',
                        visibility: (draggedCondition && groupsForRating.length === 0) ? 'visible' : 'hidden',
                        opacity: (draggedCondition && groupsForRating.length === 0) ? 1 : 0,
                        pointerEvents: (draggedCondition && groupsForRating.length === 0) ? 'auto' : 'none',
                        height: (draggedCondition && groupsForRating.length === 0) ? undefined : 0,
                        border: (draggedCondition && groupsForRating.length === 0) ? undefined : 'none',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600 }}>
                        Hier ablegen, um die erste Gruppe in dieser Farbe zu erstellen
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}

              {/* Simulation Result */}
              {simulationRating && (
                <Alert
                  severity={simulationRating === 'G' ? 'success' : simulationRating === 'Y' ? 'warning' : 'error'}
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{simulationExplanation}</Typography>
                </Alert>
              )}
              {simulationRating === null && simulationExplanation && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">{simulationExplanation}</Typography>
                </Alert>
              )}

              {/* Action Buttons */}
              {(ruleGroups.length > 0 || autoRedFallback) && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={runRuleSimulation}
                    disabled={resolvedValue === null}
                    sx={{ borderColor: colors.black, color: colors.black }}
                  >
                    Regelsimulation
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveRuleConditions}
                    disabled={savingRules || ruleGroups.length === 0}
                    sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d600' } }}
                  >
                    {savingRules ? 'Speichere...' : 'Regeln speichern'}
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Field Path Explorer Dialog ───────────────────── */}
      <Dialog open={pathDialogOpen} onClose={() => setPathDialogOpen(false)} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>Field Path Explorer</DialogTitle>
        <DialogContent>
          <Autocomplete
            disablePortal
            sx={{ mt: 1, mb: 2 }}
            options={scans}
            value={selectedScan}
            inputValue={scanSearchInput}
            onChange={(_, newValue) => {
              setSelectedScan(newValue);
              setSelectedScanId(newValue?.id ?? '');
              setScanSearchInput(newValue?.url ?? '');
            }}
            onInputChange={(_, newInput, reason) => {
              if (reason === 'input') setScanSearchInput(newInput);
              if (reason === 'clear') { setScanSearchInput(''); setSelectedScan(null); setSelectedScanId(''); }
            }}
            filterOptions={(options, state) => {
              const q = state.inputValue.trim().toLowerCase();
              if (!q) return options.slice(0, 20);
              return options.filter((o) => o.url.toLowerCase().includes(q));
            }}
            getOptionLabel={(option) => option.url}
            noOptionsText="Keine Scans gefunden"
            ListboxProps={{ style: { maxHeight: 320 } }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scan auswählen"
                size="small"
                placeholder="Domain oder URL eingeben…"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ px: '12px !important', py: '8px !important', alignItems: 'flex-start !important' }}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                    {option.url}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      📅 {new Date(option.created_at).toLocaleDateString('de-DE')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      🕐 {new Date(option.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {option.engine_name && (
                      <Chip label={`⚡ ${option.engine_name}`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                    )}
                    {option.user_name && (
                      <Typography sx={{ fontSize: 11, color: '#555' }}>
                        👤 {option.user_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          />

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Field Path Explorer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Wähle den Pfad in der Playwright-JSON-Struktur (basierend auf den letzten 20 Scans):
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {fieldPathLevels.map((level, idx) => (
                <FormControl key={idx} size="small" fullWidth>
                  <InputLabel>
                    {idx === 0 ? 'Ebene 1 (Root)' : `Ebene ${idx + 1}`}
                  </InputLabel>
                  <Select
                    value={level.selected}
                    label={idx === 0 ? 'Ebene 1 (Root)' : `Ebene ${idx + 1}`}
                    onChange={(e) => handleFieldPathSelect(idx, e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value="">— Auswählen —</MenuItem>
                    {level.keys.map((k) => (
                      <MenuItem key={k.key} value={k.key}>
                        {k.key}
                        {k.hasChildren && ' 📂'}
                        {!k.hasChildren && ` (${k.type})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              {fieldPathLoading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
            </Box>

            {computedFieldPath && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: colors.black, borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: colors.lime }}>
                  {computedFieldPath}
                </Typography>
              </Box>
            )}
          </Paper>

          {simLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Wert wird aufgelöst…</Typography>
            </Box>
          )}

          {arrayFilterPanel}
          {pickNoticeBox}
          {resolveError && <Alert severity="warning" sx={{ mb: 2 }}>{resolveError}</Alert>}
          {resolvedValue !== null && !resolveError && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  Aufgelöster Wert bei <code>{computedFieldPath}</code>:
                </Typography>
                <IconButton size="small" onClick={() => setResolvedValuePopupOpen(true)} title="Aufklappen">
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Box>
              {valueClickHint}
              <Box sx={{ p: 2, bgcolor: '#0d1117', borderRadius: 2, maxHeight: 250, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.8, border: '1px solid #21262d' }}>
                {renderResolvedContent(handlePopupKeyClick, fieldPathLoading || simLoading)}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => saveFieldPathAdjustment(false)}
              disabled={!computedFieldPath || savingPathOnly}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d600' } }}
            >
              {savingPathOnly ? 'Speichere...' : 'Änderung speichern'}
            </Button>
            <Button
              variant="contained"
              onClick={() => saveFieldPathAdjustment(true)}
              disabled={!computedFieldPath || savingPathOnly}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d600' } }}
            >
              Speichern & zu Regeln wechseln
            </Button>
          </Box>
          <Button onClick={() => setPathDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Aufgelöster Wert – Erweitertes Popup ──────────── */}
      <Dialog open={resolvedValuePopupOpen} onClose={() => setResolvedValuePopupOpen(false)} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
              Aufgelöster Wert
            </Typography>
            {computedFieldPath && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Pfad: <code>{computedFieldPath}</code>
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setResolvedValuePopupOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Back button */}
          {fieldPathLevels.some((l) => l.selected !== '') && (
            <Button
              size="small"
              variant="outlined"
              onClick={handlePopupNavigateBack}
              disabled={fieldPathLoading || simLoading}
              sx={{ mb: 2 }}
            >
              ← Eine Ebene zurück
            </Button>
          )}

          {/* Resolved value display — interaktiver JSON-Renderer */}
          {arrayFilterPanel}
          {pickNoticeBox}
          {resolveError && <Alert severity="warning" sx={{ mb: 2 }}>{resolveError}</Alert>}
          {simLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}
          {resolvedValue !== null && !resolveError && !simLoading && (
            <Box sx={{ p: 2, bgcolor: '#0d1117', borderRadius: 2, mb: 2, maxHeight: 480, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.8, border: '1px solid #21262d' }}>
              {renderResolvedContent(handlePopupKeyClick, fieldPathLoading || simLoading)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolvedValuePopupOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Regeln anpassen Dialog ─────────────────────────── */}
      <Dialog open={rulesDialogOpen} onClose={closeRulesDialog} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>Regeln anpassen</DialogTitle>
        <DialogContent>
          {rulesEditorGroups.length === 0 && (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              Noch keine Regeln vorhanden. Du kannst direkt neue Gruppen für Grün, Gelb oder Rot anlegen.
            </Alert>
          )}

          <Autocomplete
            sx={{ mt: 1, mb: 2 }}
            options={scans}
            value={selectedScan}
            inputValue={scanSearchInput}
            onChange={(_, newValue) => {
              setSelectedScan(newValue);
              setSelectedScanId(newValue?.id ?? '');
              setScanSearchInput(newValue?.url ?? '');
            }}
            onInputChange={(_, newInput, reason) => {
              if (reason === 'input') setScanSearchInput(newInput);
              if (reason === 'clear') { setScanSearchInput(''); setSelectedScan(null); setSelectedScanId(''); }
            }}
            filterOptions={(options, state) => {
              const q = state.inputValue.trim().toLowerCase();
              if (!q) return options.slice(0, 20);
              return options.filter((o) => o.url.toLowerCase().includes(q));
            }}
            getOptionLabel={(option) => option.url}
            noOptionsText="Keine Scans gefunden"
            ListboxProps={{ style: { maxHeight: 320 } }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scan für Regelsimulation"
                size="small"
                placeholder="Domain oder URL eingeben…"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ px: '12px !important', py: '8px !important', alignItems: 'flex-start !important' }}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                    {option.url}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      📅 {new Date(option.created_at).toLocaleDateString('de-DE')}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>
                      🕐 {new Date(option.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {option.engine_name && (
                      <Chip label={`⚡ ${option.engine_name}`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                    )}
                    {option.user_name && (
                      <Typography sx={{ fontSize: 11, color: '#555' }}>
                        👤 {option.user_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          />

          {/* Simulation Output — full display, exactly as before */}
          <Paper
            ref={simOutputRef}
            variant="outlined"
            sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Simulation Output
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#666', fontFamily: 'monospace', mb: 1 }}>
              Pfad: {rulesEditorFieldPath || 'kein Pfad gesetzt'}
            </Typography>
            {rulesEditorSimResolvedType && (
              <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                Datentyp: {rulesEditorSimResolvedType}
              </Typography>
            )}
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e5e7eb', mb: 1.25, maxHeight: 170, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {rulesEditorSimResolvedValue === null
                  ? 'Noch kein Simulationswert vorhanden.'
                  : typeof rulesEditorSimResolvedValue === 'string'
                    ? rulesEditorSimResolvedValue
                    : JSON.stringify(rulesEditorSimResolvedValue, null, 2)}
              </Typography>
            </Box>
            {rulesEditorSimExplanation && (
              <Alert
                severity={rulesEditorSimRating === 'G' ? 'success' : rulesEditorSimRating === 'Y' ? 'warning' : rulesEditorSimRating === 'R' ? 'error' : 'info'}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body2">{rulesEditorSimExplanation}</Typography>
              </Alert>
            )}
          </Paper>

          {/* Sticky result bar — appears only when full output has scrolled out of view */}
          {simOutputSticky && rulesEditorSimExplanation && (
            <Box sx={{ position: 'sticky', top: 0, zIndex: 10, mb: 2 }}>
              <Alert
                severity={rulesEditorSimRating === 'G' ? 'success' : rulesEditorSimRating === 'Y' ? 'warning' : rulesEditorSimRating === 'R' ? 'error' : 'info'}
                action={
                  <IconButton size="small" onClick={() => setSimStickyExpanded((v) => !v)} aria-label={simStickyExpanded ? 'Wert einklappen' : 'Wert ausklappen'}>
                    {simStickyExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                }
                sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.14)' }}
              >
                <Typography variant="body2">{rulesEditorSimExplanation}</Typography>
              </Alert>
              {simStickyExpanded && (
                <Paper elevation={3} sx={{ mt: 0.5, p: 1, borderRadius: 2, maxHeight: 150, overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.75rem' }}>
                    {rulesEditorSimResolvedValue === null
                      ? 'Kein Wert vorhanden.'
                      : typeof rulesEditorSimResolvedValue === 'string'
                        ? rulesEditorSimResolvedValue
                        : JSON.stringify(rulesEditorSimResolvedValue, null, 2)}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Regeln direkt bearbeiten
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bedingungen innerhalb einer Gruppe sind UND-verknüpft. Mehrere Gruppen pro Farbe sind ODER-verknüpft.
              Regeln können per Drag & Drop zwischen Gruppen verschoben werden.
            </Typography>

            {(['G', 'Y', 'R'] as const).map((rating) => {
              const ratingLabel = rating === 'G' ? 'Grün (OK)' : rating === 'Y' ? 'Gelb (Optimierungsbedarf)' : 'Rot (Kritisch)';
              const ratingColor = rating === 'G' ? colors.scoreGreen : rating === 'Y' ? '#b8860b' : colors.scoreRed;
              const groupsForRating = rulesEditorGroups.filter((g) => g.rating === rating);

              return (
                <Box key={rating} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ratingColor }} />
                    <Typography variant="subtitle2" sx={{ color: ratingColor }}>{ratingLabel}</Typography>
                    {groupsForRating.length === 0 && (
                      <IconButton size="small" onClick={() => addRulesEditorFirstCondition(rating)} aria-label={`${ratingLabel} Bedingung hinzufügen`}>
                        <AddIcon fontSize="small" sx={{ color: ratingColor }} />
                      </IconButton>
                    )}
                  </Box>

                  {groupsForRating.map((group, groupIdx) => (
                    <Box key={group.id}>
                      {groupIdx > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, ml: 2 }}>
                          <Box sx={{ flex: 1, height: '1px', bgcolor: ratingColor, opacity: 0.4 }} />
                          <Chip label="ODER" size="small" sx={{ bgcolor: ratingColor, color: '#fff', fontWeight: 700, fontSize: 11, height: 22 }} />
                          <Box sx={{ flex: 1, height: '1px', bgcolor: ratingColor, opacity: 0.4 }} />
                        </Box>
                      )}

                      <Paper
                        variant="outlined"
                        sx={{
                          ml: 2.5,
                          p: 1.5,
                          borderRadius: 2,
                          borderColor: ratingColor,
                          borderStyle: 'dashed',
                          bgcolor: rulesEditorDraggedCondition && rulesEditorDraggedCondition.fromGroupId !== group.id ? '#f5f5f5' : 'transparent',
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (rulesEditorDraggedCondition) {
                            moveRulesEditorCondition(rulesEditorDraggedCondition.conditionId, rulesEditorDraggedCondition.fromGroupId, group.id);
                            setRulesEditorDraggedCondition(null);
                            setRulesEditorOrDropAfterGroupId(null);
                            setRulesEditorDropRating(null);
                          }
                        }}
                      >
                        <Typography variant="caption" sx={{ color: ratingColor, fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Gruppe {groupIdx + 1} (UND-verknüpft)
                        </Typography>

                        {group.conditions.map((cond, condIdx) => (
                          <Box key={cond.id}>
                            {condIdx > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, my: 0.5 }}>
                                <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#e0e0e0' }} />
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                                p: 0.5,
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                              }}
                            >
                              <Box
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', cond.id);
                                  setTimeout(() => setRulesEditorDraggedCondition({ conditionId: cond.id, fromGroupId: group.id }), 0);
                                }}
                                onDragEnd={() => {
                                  setRulesEditorDraggedCondition(null);
                                  setRulesEditorOrDropAfterGroupId(null);
                                  setRulesEditorDropRating(null);
                                }}
                                sx={{
                                  width: 24,
                                  height: 40,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'grab',
                                  color: '#6b7280',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#f3f4f6', color: '#111827' },
                                  '&:active': { cursor: 'grabbing' },
                                }}
                                title="Regel ziehen"
                                aria-label="Regel ziehen"
                              >
                                <DragIndicatorIcon sx={{ fontSize: 18 }} />
                              </Box>

                              <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Operator</InputLabel>
                                <Select
                                  value={cond.operator}
                                  label="Operator"
                                  onChange={(e) => updateRulesEditorCondition(group.id, cond.id, 'operator', e.target.value)}
                                  renderValue={operatorLabel}
                                  MenuProps={OPERATOR_MENU_PROPS}
                                >
                                  {renderOperatorItems()}
                                </Select>
                              </FormControl>

                              {cond.operator === 'array_has_key_value' ? (
                                <>
                                  <TextField
                                    size="small"
                                    label="Key"
                                    value={cond.expected_value.includes('=') ? cond.expected_value.slice(0, cond.expected_value.indexOf('=')) : cond.expected_value}
                                    onChange={(e) => {
                                      const v = cond.expected_value.includes('=') ? cond.expected_value.slice(cond.expected_value.indexOf('=') + 1) : '';
                                      updateRulesEditorCondition(group.id, cond.id, 'expected_value', `${e.target.value}=${v}`);
                                    }}
                                    sx={{ flex: '1 1 110px' }}
                                  />
                                  <TextField
                                    size="small"
                                    label="Value"
                                    value={cond.expected_value.includes('=') ? cond.expected_value.slice(cond.expected_value.indexOf('=') + 1) : ''}
                                    onChange={(e) => {
                                      const k = cond.expected_value.includes('=') ? cond.expected_value.slice(0, cond.expected_value.indexOf('=')) : cond.expected_value;
                                      updateRulesEditorCondition(group.id, cond.id, 'expected_value', `${k}=${e.target.value}`);
                                    }}
                                    sx={{ flex: '1 1 110px' }}
                                  />
                                </>
                              ) : (
                                <TextField
                                  size="small"
                                  label="Erwarteter Wert"
                                  value={cond.expected_value}
                                  onChange={(e) => updateRulesEditorCondition(group.id, cond.id, 'expected_value', e.target.value)}
                                  sx={{ flex: '1 1 150px' }}
                                  disabled={['exists', 'not_exists', 'is_true', 'is_false', 'array_is_empty', 'array_not_empty'].includes(cond.operator)}
                                  helperText={operatorHelperText(cond.operator)}
                                />
                              )}

                              <IconButton size="small" onClick={() => removeRulesEditorCondition(group.id, cond.id)} aria-label="Bedingung entfernen">
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => addRulesEditorConditionToGroup(group.id)}
                            sx={{ fontSize: 11, textTransform: 'none', borderColor: '#bbb', color: '#555' }}
                          >
                            + UND (Bedingung hinzufügen)
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => addRulesEditorGroupAfter(group.id)}
                            sx={{ fontSize: 11, textTransform: 'none', borderColor: ratingColor, color: ratingColor }}
                          >
                            + ODER (neue Gruppe)
                          </Button>
                        </Box>
                      </Paper>

                      {/* Always rendered to avoid reflow; hidden via CSS when not dragging */}
                      <Box
                        onDragOver={(e) => {
                          if (!rulesEditorDraggedCondition) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setRulesEditorOrDropAfterGroupId(group.id);
                        }}
                        onDragLeave={() => {
                          if (rulesEditorOrDropAfterGroupId === group.id) {
                            setRulesEditorOrDropAfterGroupId(null);
                          }
                        }}
                        onDrop={(e) => {
                          if (!rulesEditorDraggedCondition) return;
                          e.preventDefault();
                          moveRulesEditorConditionToNewGroupAfter(
                            rulesEditorDraggedCondition.conditionId,
                            rulesEditorDraggedCondition.fromGroupId,
                            group.id,
                          );
                          setRulesEditorDraggedCondition(null);
                          setRulesEditorOrDropAfterGroupId(null);
                          setRulesEditorDropRating(null);
                        }}
                        sx={{
                          ml: 2.5,
                          mt: 1,
                          p: 1,
                          borderRadius: 1.5,
                          border: '1px dashed #9ca3af',
                          bgcolor: rulesEditorOrDropAfterGroupId === group.id ? '#e5e7eb' : '#f3f4f6',
                          transition: 'opacity 0.15s ease, background-color 0.15s ease',
                          visibility: rulesEditorDraggedCondition ? 'visible' : 'hidden',
                          opacity: rulesEditorDraggedCondition ? 1 : 0,
                          pointerEvents: rulesEditorDraggedCondition ? 'auto' : 'none',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600 }}>
                          Hier ablegen, um eine neue ODER-Gruppe zu erstellen
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  {/* Rating-level drop zone — only for empty colors, placed AFTER groups */}
                  <Paper
                    variant="outlined"
                    onDragOver={(e) => {
                      if (!rulesEditorDraggedCondition || groupsForRating.length > 0) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setRulesEditorDropRating(rating);
                    }}
                    onDragLeave={() => {
                      if (rulesEditorDropRating === rating) setRulesEditorDropRating(null);
                    }}
                    onDrop={(e) => {
                      if (!rulesEditorDraggedCondition || groupsForRating.length > 0) return;
                      e.preventDefault();
                      moveRulesEditorConditionToNewGroupInRating(
                        rulesEditorDraggedCondition.conditionId,
                        rulesEditorDraggedCondition.fromGroupId,
                        rating,
                      );
                      setRulesEditorDraggedCondition(null);
                      setRulesEditorOrDropAfterGroupId(null);
                      setRulesEditorDropRating(null);
                    }}
                    sx={{
                      ml: 2.5,
                      mt: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? 1 : 0,
                      p: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? 1.5 : 0,
                      borderRadius: 1.5,
                      borderStyle: 'dashed',
                      borderColor: rulesEditorDropRating === rating ? ratingColor : '#9ca3af',
                      bgcolor: rulesEditorDropRating === rating ? '#e5e7eb' : '#f3f4f6',
                      transition: 'all 0.15s ease',
                      visibility: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? 'visible' : 'hidden',
                      opacity: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? 1 : 0,
                      pointerEvents: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? 'auto' : 'none',
                      height: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? undefined : 0,
                      border: (rulesEditorDraggedCondition && groupsForRating.length === 0) ? undefined : 'none',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600 }}>
                      Hier ablegen, um die erste Gruppe in dieser Farbe zu erstellen
                    </Typography>
                  </Paper>
                </Box>
              );
            })}

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff8f8', borderColor: colors.scoreRed }}>
              <FormControlLabel
                control={<Switch checked={rulesEditorAutoRedFallback} onChange={(e) => setRulesEditorAutoRedFallback(e.target.checked)} color="error" />}
                label={<Typography variant="body2"><strong>Auto-Rot-Fallback</strong> aktiv</Typography>}
              />
            </Paper>

          </Paper>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={saveRulesDialogChanges}
              disabled={rulesEditorSaving || !rulesEditorFieldPath || rulesEditorGroups.length === 0}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d600' } }}
            >
              {rulesEditorSaving ? 'Speichere...' : 'Regeln speichern'}
            </Button>
          </Box>
          <Button onClick={closeRulesDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Check Dialog ────────────────────────────── */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus>
        <DialogTitle>Neuen Check anlegen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Checkname"
            placeholder="z.B. Pre-Consent Tracking Scripts"
            value={newCheck.title}
            autoFocus
            onChange={(e) => {
              const title = e.target.value;
              const base = slugifyCheck(title);
              const existingCodes = checks.map((c) => c.check_code);
              let candidate = base;
              let i = 2;
              while (existingCodes.includes(candidate)) candidate = `${base}_${i++}`;
              setNewCheck((prev) => ({ ...prev, title, check_code: candidate }));
            }}
            size="small"
            required
            helperText="Name des Checks"
          />
          <TextField
            label="Beschreibung"
            value={newCheck.description}
            onChange={(e) => setNewCheck((prev) => ({ ...prev, description: e.target.value }))}
            size="small"
            multiline
            rows={2}
          />
          {allLabels.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.75, display: 'block' }}>Labels</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {allLabels.map((label) => {
                  const selected = newCheck.label_ids.includes(label.id);
                  return (
                    <Chip
                      key={label.id}
                      label={label.name}
                      size="small"
                      onClick={() => setNewCheck((prev) => ({
                        ...prev,
                        label_ids: selected
                          ? prev.label_ids.filter((id) => id !== label.id)
                          : [...prev.label_ids, label.id],
                      }))}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selected ? (label.color ?? '#c8e600') : '#f3f4f6',
                        color: selected ? '#000' : '#374151',
                        fontWeight: selected ? 600 : 400,
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateCheck}
            disabled={createLoading || !newCheck.title.trim()}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d600' } }}
          >
            {createLoading ? 'Erstelle...' : 'Weiter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
